import { setLedgerRun, ledgerRunFields } from '@/lib/ledgerRun';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { AppLayout } from '@/components/AppLayout';
import { CheckCircle, TrendingUp, BarChart3, ExternalLink, Clock, FileText, Copy, Monitor, ArrowLeft, Code, ZoomIn, ZoomOut, Target, Sparkles, Download, FileDown, ChevronDown, FileType, Package, Wand2, ClipboardList } from 'lucide-react';
import { ResumeMatchAnalysisModal } from '@/components/ResumeMatchAnalysisModal';
import { ClaudeMatchAnalysisModal } from '@/components/ClaudeMatchAnalysisModal';
import { runHybridMatchAnalysis, runHybridScores } from '@/services/claudeMatchAnalysisService';
import ClaudeDirectAnalysis from '@/components/ClaudeDirectAnalysis';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { TailoredResumeService } from '@/services/tailoredResumeService';
import { TailoredDocumentService } from '@/services/tailoredDocumentService';
import { useAuth } from '@/hooks/useAuth';
import { WordOnlineViewer } from '@/components/WordOnlineViewer';
import { RichTextDisplay } from '@/components/RichTextDisplay';
import { DocumentPaper } from '@/components/DocumentPaper';
import { DocumentRichTextDisplay } from '@/components/DocumentRichTextDisplay';
import { supabase } from '@/integrations/supabase/client';
import { exportToPDF } from '@/utils/pdfExportService';
import { BulkResumeService } from '@/services/bulkResumeService';
import { BulkLetterService } from '@/services/bulkLetterService';
import { CreditService } from '@/services/creditService';
import { CustomTemplateService, CustomTemplate } from '@/services/customTemplateService';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { TemplateFamilyMenu } from '@/components/TemplateFamilyMenu';

interface TailoredDocument {
  id: string;
  type: 'resume' | 'cover-letter' | 'thank-you-letter' | 'executive-bio' | 'linkedin-profile' | 'outreach-letter';
  title: string;
  content: string;
  createdAt: Date;
  jobTitle?: string;
  company?: string;
  file_name?: string; // Add the saved filename property
}

import { PaginatedTemplate1 } from '@/components/ResumeTemplates/PaginatedTemplate1';
import { PaginatedTemplate2 } from '@/components/ResumeTemplates/PaginatedTemplate2';
import { PaginatedTemplate3 } from '@/components/ResumeTemplates/PaginatedTemplate3';
import { PaginatedTemplate4 } from '@/components/ResumeTemplates/PaginatedTemplate4';
import { PaginatedTemplate5 } from '@/components/ResumeTemplates/PaginatedTemplate5';
import { Template1ResumeData } from '@/components/ResumeTemplates/Template1';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

// Letter and Bio Templates
import { 
  LetterTemplate1, LetterTemplate2, LetterTemplate3, LetterTemplate4, LetterTemplate5,
  BioTemplate1, BioTemplate2, BioTemplate3, BioTemplate4, BioTemplate5,
  parseLetterContent, parseExecutiveBioContent,
  convertVectorShiftToLetterData, VectorShiftLetterData
} from '@/components/LetterTemplates';
import {
  ExecutiveBioNavy, exportExecutiveBioPdf, parseStructuredBio,
} from '@/components/ExecutiveBioNavy';

// NOTE: Sample/template preview data removed.
// This page should only display outputs returned by VectorShift (or user-provided JSON via the "Load JSON" dialog).

// === SAFE STRING HELPERS ===
// These helpers normalize potentially non-string content (e.g., JSON objects from VectorShift) to strings
// IMPORTANT: Only use these for content that MUST be a string (documents array, copy operations)
// Do NOT use on Resume_JSON or *_JSON fields that need to stay as objects for templates
const safeString = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  // VectorShift letter objects have a Body field - extract it
  if (typeof value === 'object' && value.Body && typeof value.Body === 'string') {
    return value.Body;
  }
  // For other objects, return empty string (don't stringify for user-facing content)
  if (typeof value === 'object') {
    console.warn('safeString received object without Body field:', Object.keys(value));
    return '';
  }
  return String(value);
};

const safePreview = (value: any, maxLength: number = 200): string => {
  const str = safeString(value);
  return str.substring(0, maxLength);
};

// Check if a value looks like VectorShift letter JSON (has Body, Name, etc)
const isVectorShiftLetterJSON = (value: any): boolean => {
  return value && typeof value === 'object' && 
         typeof value.Body === 'string' && 
         typeof value.Name === 'string';
};

// Check if a value looks like VectorShift LinkedIn JSON (has LinkedIn_About, LinkedIn_Experience, etc.)
const isVectorShiftLinkedInJSON = (value: any): boolean => {
  return value && typeof value === 'object' && (
    typeof value.LinkedIn_About === 'string' ||
    typeof value.LinkedIn_Experience === 'string' ||
    typeof value.LinkedIn_Headline_1 === 'string'
  );
};

// Check if a value looks like resume JSON (VectorShift sometimes returns this under Final_Resume_Text)
const isResumeJsonLike = (value: any): boolean => {
  return !!(
    value &&
    typeof value === 'object' &&
    typeof value.Name === 'string' &&
    (Array.isArray(value.SkillRows) || Array.isArray(value.Skills)) &&
    Array.isArray(value.Education) &&
    Array.isArray(value.ProfessionalExperience)
  );
};

// Flatten Claude unified JSON { clientInfo, documents } into flat Docmosis-compatible format
const flattenClaudeUnifiedJson = (data: any): any => {
  if (!data?.clientInfo || !Array.isArray(data?.documents)) return data;
  
  const clientInfo = data.clientInfo;
  const resumeDoc = data.documents.find((d: any) => d.documentType === 'resume');
  if (!resumeDoc?.data) return data;
  
  const [city, state] = (clientInfo.cityST || '').split(',').map((s: string) => s.trim());
  return {
    ...resumeDoc.data,
    Name: resumeDoc.data.Name || clientInfo.fullName,
    Phone: resumeDoc.data.Phone || clientInfo.phoneNumber,
    Email: resumeDoc.data.Email || clientInfo.email,
    City: resumeDoc.data.City || city || clientInfo.cityST || '',
    State: resumeDoc.data.State || state || '',
    LinkedIn: resumeDoc.data.LinkedIn || clientInfo.linkedin,
  };
};

// Docmosis template option metadata. Add a new template here and the
// dropdown + backend mapping (`generate-bulk-resumes`) pick it up automatically.
// Names reflect the TYPE/style, not the .docx file name.
type DocmosisTemplateId =
  | 'template1' | 'template2' | 'template3'
  | 'template4' | 'template5' | 'template6'
  | 'template7' | 'template8' | 'template9'
  | 'rdr-blue-theme-v1.docx' | 'rdr-blue-theme-v3.docx'
  | `rdr-blue-theme-v3-${string}.docx`;

// The client's OWN design, rebuilt with the new content (Original Format Clone
// Playbook). Offered only when the run has the client's Word (.docx) file.
const ORIGINAL_FORMAT_OPTION = { id: 'original-format', label: 'Original Format (your design)', subtitle: 'Your own resume design, rebuilt with the new content' } as const;

const DOCMOSIS_TEMPLATE_OPTIONS: Array<{
  id: DocmosisTemplateId;
  label: string;
  subtitle: string;
}> = [
  { id: 'rdr-blue-theme-v3.docx', label: 'RDR Blue (Default)', subtitle: 'Navy — built from the approved RDR reference resume' },
  { id: 'rdr-blue-theme-v3-burgundy.docx', label: 'RDR Burgundy', subtitle: 'Same RDR design, burgundy bars and banner' },
  { id: 'rdr-blue-theme-v3-forest.docx', label: 'RDR Forest Green', subtitle: 'Same RDR design, forest green bars and banner' },
  { id: 'rdr-blue-theme-v3-teal.docx', label: 'RDR Teal', subtitle: 'Same RDR design, teal bars and banner' },
  { id: 'rdr-blue-theme-v3-plum.docx', label: 'RDR Plum', subtitle: 'Same RDR design, plum bars and banner' },
  { id: 'rdr-blue-theme-v3-black.docx', label: 'RDR Black', subtitle: 'Same RDR design, black bars and banner' },
];

const docmosisTemplateLabel = (id: string): string =>
  id === ORIGINAL_FORMAT_OPTION.id ? ORIGINAL_FORMAT_OPTION.label : (DOCMOSIS_TEMPLATE_OPTIONS.find((o) => o.id === id)?.label ?? id);

const ResumeResultsTemplated: React.FC = () => {
  const rawLocation = useLocation();
  // REFRESH RULE (Marcus, Oct 25 2026): once a run's resume has been fitted and
  // saved, a page refresh must show THAT saved resume, not replay the run from
  // the browser's navigation state (which re-rendered the unfitted draft and
  // ran the fitting again). The flag is set only after the fitting finished
  // and the fitted file was saved; until then a refresh continues the run.
  // Decided once, when the page opens: this tab may have been reloaded earlier on
  // another page, and the URL gains ?sessionId= during a live run (Nov 4 2026).
  const [finishedOnReload] = useState(() => {
    try {
      const sid = new URLSearchParams(window.location.search).get('sessionId');
      const nav = (performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined)?.type;
      // Any refresh of a saved session shows the saved document; the run is never replayed.
      return !!sid && nav === 'reload';
    } catch { return false; }
  });
  const location = finishedOnReload ? { ...rawLocation, state: null } : rawLocation;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();

  // State for persistence and loading
  const [isLoadingFromDatabase, setIsLoadingFromDatabase] = useState(false);
  const [persistedSession, setPersistedSession] = useState<any>(null);
  const [runIdError, setRunIdError] = useState<string | null>(null);
  const [sessionTimestamp, setSessionTimestamp] = useState<Date | null>(null);
  // Library re-open (?sessionId=): the envelope saved with the session, and
  // the persisted stage-4 analysis so re-opening never re-runs (or re-bills) it.
  const [dbResultsState, setDbResultsState] = useState<any>(null);
  const [dbStage4, setDbStage4] = useState<any>(null);
  const [savedSessionRowId, setSavedSessionRowId] = useState<string | null>(null);
  const savedSessionRowIdRef = useRef<string | null>(null);
  useEffect(() => { savedSessionRowIdRef.current = savedSessionRowId; }, [savedSessionRowId]);
  // Analysis can finish before the auto-save has a row to write to; park it
  // here and the save flow flushes it once the session row exists.
  const pendingStage4Ref = useRef<any>(null);

  // Persist the finished stage-4 analysis onto the session's resume row so a
  // library re-open shows it instantly instead of re-running a 30s Claude call.
  const persistStage4 = React.useCallback(async (analysis: any) => {
    if (analysis) pendingStage4Ref.current = analysis;
    const rowId = savedSessionRowIdRef.current;
    const toWrite = pendingStage4Ref.current;
    if (!rowId || !toWrite) return;
    try {
      const { data: row } = await supabase
        .from('tailored_documents')
        .select('analysis_results')
        .eq('id', rowId)
        .single();
      const merged = { ...(row?.analysis_results as Record<string, unknown> ?? {}), _stage4: toWrite };
      // The library's score circle reads these COLUMNS. Claude Direct computes
      // scores here in stage 4 — after the session row was created — so the
      // columns start null ("No score" cards) until this write lands.
      const before = toWrite?.scores?.before;
      const after = toWrite?.scores?.after;
      await supabase.from('tailored_documents').update({
        analysis_results: merged,
        ...(typeof after === 'number' ? { match_score: after } : {}),
        ...(typeof before === 'number' ? { original_match_score: before } : {}),
      }).eq('id', rowId);
      pendingStage4Ref.current = null;
      console.log('Stage-4 analysis + scores persisted to session row', rowId, { before, after });
    } catch (e) {
      console.warn('Could not persist stage-4 analysis:', e);
    }
  }, []);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showSessionDetails, setShowSessionDetails] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const savePromiseRef = useRef<Promise<void> | null>(null);
  
  // State for JSON conversion and template display
  const [jsonResumeData, setJsonResumeData] = useState<any>(null);
  const [isConvertingToJson, setIsConvertingToJson] = useState(false);
  const [jsonConversionError, setJsonConversionError] = useState<string | null>(null);
  const [showJsonDialog, setShowJsonDialog] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [selectedTemplate, setSelectedTemplate] = useState<'template1' | 'template2' | 'template3' | 'template4' | 'template5' | 'template6' | 'template7' | 'template8' | 'template9' | 'template4' | 'template5'>('template1');
  
  // Timer state for TEMPLATE CONVERSION tracking (not tailoring duration)
  const [conversionStartTime, setConversionStartTime] = useState<number | null>(null);
  const [conversionElapsedTime, setConversionElapsedTime] = useState(0);
  
  // State for tailoring results and selected documents
  const [tailoringResults, setTailoringResults] = useState<any>(location.state?.tailoringResults || null);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>(location.state?.selectedDocuments || ['resume']);
  const [isFromDatabase, setIsFromDatabase] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isFitting, setIsFitting] = useState(false);
  // Every line the last fit run edited: label + before/after text, so the
  // user can open exactly what was adjusted.
  const [fitChanges, setFitChanges] = useState<Array<{ label: string; before: string; after: string }>>([]);
  const [fitEngine, setFitEngine] = useState<string>('');
  const [showFitChanges, setShowFitChanges] = useState(false);
  // Auto-fix formatting: run Layout QA once, unprompted, after a FRESH
  // generation finishes (never on library re-opens). Defaults ON; the user can
  // turn it off per run with the switch on the tailoring page.
  const autoFixFormatting: boolean = (location.state as any)?.autoFixFormatting !== false;
  // Cost ledger: follow-up calls from this page count toward the run that made it.
  useEffect(() => { setLedgerRun((location.state as any)?.ledgerRun ?? null); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // The client's own Word file (from the upload or the saved resume), if the run has one.
  // A Word file uploaded right here (for runs that started from a PDF or pasted text).
  const [uploadedOriginalUrl, setUploadedOriginalUrl] = useState<string | null>(null);
  const originalUploadRef = useRef<HTMLInputElement | null>(null);
  const originalDocxUrl: string | null = uploadedOriginalUrl || ((location.state as any)?.originalDocxUrl as string | undefined) || (dbResultsState as any)?.originalDocxUrl || null;
  useEffect(() => { BulkResumeService.originalDocxUrl = originalDocxUrl; }, [originalDocxUrl]);
  const autoFitAttempted = useRef(false);
  // Flips true once the unprompted Layout QA pass has finished (fixed, failed
  // or skipped). The match analysis waits for it, so the score is computed
  // from the FINAL document the user downloads — never an earlier draft.
  const [autoFitFinished, setAutoFitFinished] = useState(false);

  // Forwarded from the tailoring page when Deep Tailor produced a Tailoring
  // Report. Used by the Solution Asset and Hiring Manager Contact Report
  // producers. Empty strings/arrays when Quick Tailor was used.
  const tailoringReportPainPoints: string = (location.state as any)?.tailoringReportPainPoints || '';
  const [showBuildCriteria, setShowBuildCriteria] = useState(true);
  // deno-lint-ignore no-explicit-any
  const hiringManagerCandidates: any[] = Array.isArray((location.state as any)?.hiringManagerCandidates)
    ? (location.state as any).hiringManagerCandidates
    : [];
  const originalResumeText: string = (location.state as any)?.originalResume || dbResultsState?.originalResume || '';

  // Gates document generation + the preview tab strip on what the user actually
  // selected in the wizard. Accepts both hyphenated (`cover-letter`) and
  // camelCase (`coverLetter`) names — both shapes appear in the codebase.
  const wasSelected = (...keys: string[]): boolean =>
    keys.some((k) => selectedDocuments.includes(k));

  // ── Hybrid scoring: VectorShift writes the documents, Claude computes the
  // before/after match scores + analysis when VectorShift didn't supply them.
  const [claudeMatch, setClaudeMatch] = useState<{ beforeScore: number | null; afterScore: number | null; analysisResults: any }>({ beforeScore: null, afterScore: null, analysisResults: null });
  const [claudeMatchLoading, setClaudeMatchLoading] = useState(false);
  const claudeMatchAttempted = useRef(false);

  // ── Background companion letters (stage 8) ────────────────────────────────
  // The tailoring page no longer waits for letters before navigating here.
  // If letter inputs were forwarded and the letter JSONs aren't present yet,
  // generate them now in the background and merge into tailoringResults — the
  // existing letter document producers pick them up automatically.
  const [lettersGenerating, setLettersGenerating] = useState(false);
  const lettersAttempted = useRef(false);
  const lettersSettled = useRef(false); // true once background letters finished (success OR failure)
  useEffect(() => {
    const inputs = (location.state as any)?.letterGenInputs;
    if (!inputs || lettersAttempted.current || !tailoringResults) return;
    if (tailoringResults.Cover_Letter_JSON || tailoringResults.Thank_You_Letter_JSON ||
        tailoringResults.Outreach_Email_JSON) return; // already generated
    lettersAttempted.current = true;
    setLettersGenerating(true);
    (async () => {
      try {
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token;
        if (!token) throw new Error('Not signed in');
        const res = await fetch('https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/tailor-resume-claude-jd', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I' },
          body: JSON.stringify({ stage: 8, ...inputs, ...ledgerRunFields() }),
        });
        const j = await res.json();
        if (!res.ok || !j?.success) throw new Error(j?.error || 'Letter generation failed');
        setTailoringResults((prev: any) => ({
          ...prev,
          ...(j.Cover_Letter_JSON ? { Cover_Letter_JSON: j.Cover_Letter_JSON } : {}),
          ...(j.Thank_You_Letter_JSON ? { Thank_You_Letter_JSON: j.Thank_You_Letter_JSON } : {}),
          ...(j.Outreach_Email_JSON ? { Outreach_Email_JSON: j.Outreach_Email_JSON, Outreach_Letter_JSON: j.Outreach_Email_JSON } : {}),
          ...(j.Executive_Bio ? { Executive_Bio: j.Executive_Bio } : {}),
        }));
        toast({ title: 'Companion documents ready', description: 'Your letters were generated and added to the document tabs.' });
      } catch (e: any) {
        console.error('Background letters failed:', e);
        toast({ title: 'Companion documents failed', description: e?.message || 'The resume is unaffected. Re-run tailoring to retry letters.', variant: 'destructive' });
      } finally {
        lettersSettled.current = true;
        setLettersGenerating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tailoringResults]);

  useEffect(() => {
    if (!tailoringResults || claudeMatchAttempted.current) return;
    // Claude Direct engine has its own richer Tailoring Analysis (scores + placement);
    // don't run the generic scorer for it or we'd show two conflicting sets of numbers.
    if (location.state?.claudeDirectAnalysis) return;
    // Only run when VectorShift did NOT already return both scores.
    const hasVsScores = tailoringResults.Original_Match_Score != null && tailoringResults.Tailored_Match_Score != null;
    if (hasVsScores) return;

    const originalResumeText = location.state?.originalResume;
    const tailoredResumeText = tailoringResults.Final_Resume_Text || tailoringResults.Resume_JSON;
    const jobDescriptionText = location.state?.jobDescription;
    if (!originalResumeText || !tailoredResumeText || !jobDescriptionText) return;

    claudeMatchAttempted.current = true;
    setClaudeMatchLoading(true);
    const args = {
      originalResumeText,
      tailoredResumeText,
      jobDescriptionText,
      jobTitle: tailoringResults.job_title,
      companyName: tailoringResults.company_name,
    };
    // Phase 1 (fast, ~2-3s): scores only -> before/after donuts appear immediately.
    runHybridScores(args)
      .then((fast) => {
        if (fast.beforeScore != null) {
          setClaudeMatch((prev) => ({
            beforeScore: fast.beforeScore,
            afterScore: fast.afterScore,
            analysisResults: prev.analysisResults || fast.analysisResults,
          }));
        }
      })
      .catch((e) => console.error('[ResumeResults] fast scores failed:', e))
      .finally(() => {
        // Phase 2 (background): full analysis -> detailed modal content.
        runHybridMatchAnalysis(args)
          .then((full) => {
            if (full.analysisResults) {
              setClaudeMatch((prev) => ({
                beforeScore: prev.beforeScore ?? full.beforeScore,
                afterScore: prev.afterScore ?? full.afterScore,
                analysisResults: full.analysisResults,
              }));
            }
          })
          .catch((e) => console.error('[ResumeResults] full analysis failed:', e))
          .finally(() => setClaudeMatchLoading(false));
      });
  }, [tailoringResults]);
  
  // State for auto-generated Docmosis Word documents with PUBLIC URLs for Word Online viewer
  const [generatedWordDocs, setGeneratedWordDocs] = useState<{
    resume?: { filename: string; data: string; size: number; publicUrl?: string };
    coverLetter?: { filename: string; data: string; size: number; publicUrl?: string };
    thankYouLetter?: { filename: string; data: string; size: number; publicUrl?: string };
    outreachLetter?: { filename: string; data: string; size: number; publicUrl?: string };
    linkedin?: { filename: string; data: string; size: number; publicUrl?: string };
  }>({});
  const [isGeneratingDocmosis, setIsGeneratingDocmosis] = useState(false);
  const [docmosisGenerationError, setDocmosisGenerationError] = useState<string | null>(null);
  const [docmosisGenerationAttempted, setDocmosisGenerationAttempted] = useState(false);
  const [activeDocType, setActiveDocType] = useState<'resume' | 'coverLetter' | 'thankYouLetter' | 'outreachLetter' | 'linkedin' | 'solutionAsset' | 'hiringManagerReport' | 'executiveBio'>('resume');

  // Navy executive bio (structured one-page design)
  const [bioPhoto, setBioPhoto] = useState<string | undefined>(undefined);
  const [bioFitOk, setBioFitOk] = useState<boolean | null>(null);
  const [bioExporting, setBioExporting] = useState(false);
  const bioPageRef = useRef<HTMLDivElement>(null);
  
  // State for Docmosis template selection - Default to template2 (Executive Serif Navy).
  // String type accommodates the 9 hardcoded ids (template1..template9) AND custom
  // template UUIDs published from the Wizard.
  // Opens in the design picked on the dashboard (RDR Blue or the client's own).
  const [selectedDocmosisTemplate, setSelectedDocmosisTemplate] = useState<string>(() =>
    (location.state as any)?.initialTemplate === 'original-format' && (location.state as any)?.originalDocxUrl ? 'original-format' : 'rdr-blue-theme-v3.docx');
  const isOriginalFormat = selectedDocmosisTemplate === ORIGINAL_FORMAT_OPTION.id;
  // Custom (Wizard-published) templates fetched from custom_resume_templates table.
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  React.useEffect(() => {
    let mounted = true;
    CustomTemplateService.getUserTemplates()
      .then((rows) => { if (mounted) setCustomTemplates(rows); })
      .catch((err) => console.warn('Failed to fetch custom templates:', err));
    return () => { mounted = false; };
  }, []);
  // True UUID format = custom template. Hardcoded ids look like "template3".
  const isCustomTemplateId = (id: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  
  // Ref for the document preview container (used for PDF export)
  const documentPreviewRef = useRef<HTMLDivElement>(null);

  // Scroll to top when component mounts and set timestamp for fresh navigation state
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // For fresh navigation state, set the timestamp to now (when results were just generated)
    if (location.state?.tailoringResults && !sessionTimestamp) {
      setSessionTimestamp(new Date());
    }
  }, []);

  // Phase 1: Support explicit ?runId= query param for deterministic run viewing
  React.useEffect(() => {
    const runIdParam = searchParams.get('runId');
    if (!runIdParam) return; // No runId param, skip
    if (tailoringResults && !isFromDatabase) return; // Already have fresh navigation state

    const fetchRunById = async () => {
      setIsLoadingFromDatabase(true);
      setRunIdError(null);
      try {
        const { data, error } = await supabase
          .from('vectorshift_runs')
          .select('*')
          .eq('id', runIdParam)
          .single();

        if (error) throw error;
        if (!data) {
          setRunIdError(`Run not found: ${runIdParam}`);
          return;
        }

        // Validate user ownership
        if (user && data.user_id !== user.id) {
          setRunIdError(`Unauthorized: This run belongs to a different user.`);
          return;
        }

        // Use the response_payload as tailoringResults
        const payload = typeof data.response_payload === 'object' && data.response_payload !== null
          ? data.response_payload
          : {};
        setTailoringResults({
          ...(payload as Record<string, any>),
          run_id: data.id,
          _source: 'database(runId)',
          _run_user_id: data.user_id,
          tailoring_duration_seconds: data.duration_ms ? Math.floor(data.duration_ms / 1000) : null,
        });
        setIsFromDatabase(true);
        // Set the session timestamp from the database record
        if (data.created_at) {
          setSessionTimestamp(new Date(data.created_at));
        }
      } catch (err: any) {
        console.error('Error fetching run by ID:', err);
        setRunIdError(err.message || 'Failed to load run');
      } finally {
        setIsLoadingFromDatabase(false);
      }
    };

    fetchRunById();
  }, [searchParams, user]);

  // Library re-open: ?sessionId= loads a saved tailoring session's resume row
  // and rebuilds the full results screen from the _results_page envelope the
  // auto-save stored. Sessions saved before the envelope existed hydrate
  // honestly from what WAS saved (content, json, scores) — the analysis
  // section reports itself unavailable rather than re-deriving anything.
  React.useEffect(() => {
    const sessionIdParam = searchParams.get('sessionId');
    if (!sessionIdParam) return;
    if (tailoringResults && !isFromDatabase) return;
    if (!user) return;

    const fetchSession = async () => {
      setIsLoadingFromDatabase(true);
      setRunIdError(null);
      try {
        const { data, error } = await supabase
          .from('tailored_documents')
          .select('*')
          .eq('tailoring_session_id', sessionIdParam)
          .eq('document_type', 'resume')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (!data) {
          setRunIdError(`Tailoring session not found: ${sessionIdParam}`);
          return;
        }
        const ar: any = data.analysis_results ?? {};
        const envelope = ar?._results_page ?? null;
        if (envelope?.tailoringResults) {
          setTailoringResults({
            ...envelope.tailoringResults,
            // The latest saved data wins over the copy taken when the run ended:
            // fits and editor changes live in document_json_data.
            Resume_JSON: data.document_json_data ?? envelope.tailoringResults.Resume_JSON,
            _source: 'database(sessionId)',
            tailoring_duration_seconds: ar?.metadata?.tailoring_duration_seconds ?? null,
          });
          setDbResultsState(envelope);
          if (data.document_json_data) setJsonResumeData(data.document_json_data);
          // Saved documents: a library re-open shows the EXACT files the run
          // produced — no regeneration, no Layout QA, no analysis call.
          const savedDocs: any = ar?._docs ?? null;
          if (savedDocs && typeof savedDocs === 'object') {
            const hydrated: any = {};
            for (const k of ['resume', 'coverLetter', 'thankYouLetter', 'outreachLetter', 'linkedin']) {
              const d = savedDocs[k];
              if (d?.publicUrl) hydrated[k] = { filename: d.filename || `${k}.docx`, data: '', size: d.size ?? 0, publicUrl: d.publicUrl };
            }
            if (Object.keys(hydrated).length) setGeneratedWordDocs(hydrated);
            if (typeof savedDocs.template === 'string' && savedDocs.template) setSelectedDocmosisTemplate(savedDocs.template);
          } else if (data.word_document_url) {
            setGeneratedWordDocs({
              resume: { filename: data.file_name || 'resume.docx', data: '', size: 0, publicUrl: data.word_document_url },
            });
          }
        } else {
          // Legacy session (pre-envelope): rebuild from the columns that exist.
          setTailoringResults({
            Final_Resume_Text: data.content,
            Resume_JSON: null,
            job_title: data.job_title,
            company_name: data.company_name,
            Original_Match_Score: data.original_match_score,
            Tailored_Match_Score: data.match_score,
            original_analysis: ar?.original_analysis ?? null,
            tailored_analysis: ar?.tailored_analysis ?? null,
            improvement_report: ar?.improvement_report ?? null,
            _source: 'database(sessionId-legacy)',
            tailoring_duration_seconds: ar?.metadata?.tailoring_duration_seconds ?? null,
          });
          if (data.document_json_data) setJsonResumeData(data.document_json_data);
          // Saved documents: a library re-open shows the EXACT files the run
          // produced — no regeneration, no Layout QA, no analysis call.
          const savedDocs: any = ar?._docs ?? null;
          if (savedDocs && typeof savedDocs === 'object') {
            const hydrated: any = {};
            for (const k of ['resume', 'coverLetter', 'thankYouLetter', 'outreachLetter', 'linkedin']) {
              const d = savedDocs[k];
              if (d?.publicUrl) hydrated[k] = { filename: d.filename || `${k}.docx`, data: '', size: d.size ?? 0, publicUrl: d.publicUrl };
            }
            if (Object.keys(hydrated).length) setGeneratedWordDocs(hydrated);
            if (typeof savedDocs.template === 'string' && savedDocs.template) setSelectedDocmosisTemplate(savedDocs.template);
          } else if (data.word_document_url) {
            setGeneratedWordDocs({
              resume: { filename: data.file_name || 'resume.docx', data: '', size: 0, publicUrl: data.word_document_url },
            });
          }
        }
        if (ar?._stage4) setDbStage4(ar._stage4);
        setSavedSessionRowId(data.id);
        setIsFromDatabase(true);
        if (data.created_at) setSessionTimestamp(new Date(data.created_at));
      } catch (err: any) {
        console.error('Error fetching tailoring session:', err);
        setRunIdError(err.message || 'Failed to load tailoring session');
      } finally {
        setIsLoadingFromDatabase(false);
      }
    };
    fetchSession();
  }, [searchParams, user]);


  // Load JSON data from user input
  const handleLoadJson = () => {
    try {
      const parsed = JSON.parse(jsonInput.trim());
      setJsonResumeData(parsed);
      setJsonConversionError(null);
      setShowJsonDialog(false);
      setJsonInput('');
      toast({
        title: 'JSON loaded successfully',
        description: 'Your custom resume data has been loaded into the template.'
      });
    } catch (error) {
      toast({
        title: 'Invalid JSON',
        description: error instanceof Error ? error.message : 'Failed to parse JSON data.',
        variant: 'destructive'
      });
    }
  };

  // Convert resume text to JSON using Gemini - returns the parsed data
  const convertResumeToJson = async (): Promise<any | null> => {
    // Normalize the resume content to a string first.
    // VectorShift may return resume JSON under Final_Resume_Text, so prefer the plain-text field when present.
    const resumeContentStr =
      safeString(tailoringResults?.Tailored_Resume_Plain_Text) ||
      (typeof tailoringResults?.Final_Resume_Text === 'string'
        ? tailoringResults.Final_Resume_Text
        : '');

    if (!resumeContentStr) {
      toast({
        title: 'No resume content',
        description: 'Resume content is not available for conversion.',
        variant: 'destructive'
      });
      return null;
    }

    // Start timer for conversion duration tracking
    const startTime = Date.now();
    setConversionStartTime(startTime);
    setConversionElapsedTime(0);
    
    setIsConvertingToJson(true);
    setJsonConversionError(null);

    try {
      console.log('Converting resume to JSON...');
      const { data, error } = await supabase.functions.invoke('parse-resume-to-json', {
        body: { 
          resumeContent: resumeContentStr, // Use normalized string
          documentType: 'resume'
        }
      });

      if (error) throw error;

      if (data?.success && data?.parsedData) {
        setJsonResumeData(data.parsedData);
        console.log('Successfully converted resume to JSON:', data.parsedData);
        
        // Calculate duration
        const durationMs = startTime ? Date.now() - startTime : 0;
        const durationMinutes = Math.floor(durationMs / 60000);
        const durationSeconds = Math.floor((durationMs % 60000) / 1000);
        const durationText = durationMinutes > 0 
          ? `${durationMinutes}m ${durationSeconds}s`
          : `${durationSeconds}s`;
        
        toast({
          title: 'Resume converted successfully',
          description: `Completed in ${durationText}`
        });
        
        return data.parsedData;
      } else {
        throw new Error(data?.error || 'Failed to parse resume');
      }
    } catch (error: any) {
      console.error('Error converting resume to JSON:', error);
      setJsonConversionError(error.message || 'Failed to convert resume');
      toast({
        title: 'Conversion failed',
        description: error.message || 'Failed to convert resume to structured format.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setIsConvertingToJson(false);
    }
  };

  // Timer effect - updates elapsed time every second during TEMPLATE CONVERSION
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isConvertingToJson && conversionStartTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - conversionStartTime) / 1000);
        setConversionElapsedTime(elapsed);
      }, 1000);
    } else if (!isConvertingToJson && conversionElapsedTime > 0) {
      // Keep the final elapsed time when conversion completes
      if (interval) clearInterval(interval);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isConvertingToJson, conversionStartTime]);

  // Auto-use Resume_JSON from Claude/VectorShift - flatten unified format for templates
  React.useEffect(() => {
    // PRIORITY 1: If Resume_JSON provided directly, flatten if needed and use it
    if (tailoringResults?.Resume_JSON) {
      console.log('✅ Using Resume_JSON directly — flattening if Claude unified format');
      setJsonResumeData(flattenClaudeUnifiedJson(tailoringResults.Resume_JSON));
      return;
    }

    // PRIORITY 2: Sometimes returns resume JSON under Final_Resume_Text (object)
    if (isResumeJsonLike(tailoringResults?.Final_Resume_Text)) {
      console.log('✅ Using Final_Resume_Text as resume JSON');
      setJsonResumeData(tailoringResults.Final_Resume_Text);
      return;
    }
    
    // NO FALLBACK: If no Resume_JSON provided, show error
    if (tailoringResults && !tailoringResults?.Resume_JSON && !isResumeJsonLike(tailoringResults?.Final_Resume_Text)) {
      console.error('❌ No Resume_JSON provided - cannot display resume template');
      setJsonConversionError('Resume JSON not provided. Resume template cannot be displayed.');
    }
  }, [tailoringResults?.Final_Resume_Text, tailoringResults?.Resume_JSON]);

  // DISABLED: Do NOT auto-load from database when no location.state is present.
  // The page should ONLY display fresh VectorShift results passed via navigation.
  // Loading "most recent session" caused confusion when users expected NEW results
  // but saw OLD data from a previous session (e.g., "Steve Carrillo" data).
  //
  // If a user wants to view a saved session, they should use the document library.
  // This page is for viewing FRESH tailoring results only.

  // Use persisted session if available
  React.useEffect(() => {
    if (persistedSession && !tailoringResults) {
      setTailoringResults(persistedSession);
      setIsFromDatabase(true);
    }
  }, [persistedSession]);

  console.log('=== RESUME RESULTS DEBUG ===');
  console.log('location.state:', location.state);
  console.log('tailoringResults:', tailoringResults);
  console.log('tailoringResults.company_name:', tailoringResults?.company_name);
  console.log('tailoringResults.job_title:', tailoringResults?.job_title);
  console.log('selectedDocuments:', selectedDocuments);
  console.log('Full tailoringResults structure:', JSON.stringify(tailoringResults, null, 2));

// removed early fallback (moved below hooks)

  // Parse analysis data if it's a JSON string from VectorShift
  const parseAnalysisData = (data: any, label: string) => {
    if (!data) return null;
    console.log(`Parsing ${label}:`, typeof data);
    
    // If it's already an object, return as-is
    if (typeof data === 'object') {
      return data;
    }
    
    // If it's a string, try to parse as JSON
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        console.log(`Successfully parsed ${label} from string to object`);
        return parsed;
      } catch (e) {
        console.warn(`Failed to parse ${label} as JSON:`, e);
        return null;
      }
    }
    
    return data;
  };

  const originalAnalysis = parseAnalysisData(tailoringResults?.original_analysis, 'original_analysis');
  const tailoredAnalysis = parseAnalysisData(tailoringResults?.tailored_analysis, 'tailored_analysis');
  const changesAnalysis = parseAnalysisData(tailoringResults?.changes_analysis, 'changes_analysis');
  
  // New comprehensive analysis results structure
  const analysisResults = parseAnalysisData(tailoringResults?.analysis_results, 'analysis_results');

  console.log('=== ANALYSIS PARSING RESULTS ===');
  console.log('originalAnalysis:', originalAnalysis);
  console.log('tailoredAnalysis:', tailoredAnalysis);
  console.log('changesAnalysis:', changesAnalysis);

  // Scores: prefer VectorShift's, fall back to the Claude hybrid analysis.
  const beforeScore = (tailoringResults?.Original_Match_Score ?? claudeMatch.beforeScore) ?? null;
  const afterScore = (tailoringResults?.Tailored_Match_Score ?? claudeMatch.afterScore) ?? null;
  const improvement = (beforeScore !== null && afterScore !== null) ? afterScore - beforeScore : null;
  
  // Data source indicator for debugging
  const dataSource = tailoringResults?._source || (isFromDatabase ? 'database' : 'navigation_state');
  const runId = tailoringResults?.run_id || null;
  const displayedResumeName =
    tailoringResults?.Resume_JSON?.Name ||
    jsonResumeData?.Name ||
    (isResumeJsonLike(tailoringResults?.Final_Resume_Text) ? tailoringResults?.Final_Resume_Text?.Name : null);

  console.log('SCORES:', { beforeScore, afterScore, improvement });
  console.log('Data source:', dataSource, 'Run ID:', runId);
  console.log('Direct match scores from VectorShift:', {
    Original_Match_Score: tailoringResults?.Original_Match_Score,
    Tailored_Match_Score: tailoringResults?.Tailored_Match_Score
  });

  // Debug what's actually in tailoringResults
  console.log('=== DEBUGGING COMPANY/TITLE ===');
  console.log('tailoringResults keys:', Object.keys(tailoringResults || {}));
  console.log('tailoringResults.company_name:', tailoringResults?.company_name);
  console.log('tailoringResults.job_title:', tailoringResults?.job_title);
  console.log('Full tailoringResults:', tailoringResults);
  
  // Extract company_name and job_title - NO FALLBACKS
  const companyName = tailoringResults?.company_name;
  const jobTitle = tailoringResults?.job_title;
  const jobDescription = location.state?.jobDescription || dbResultsState?.jobDescription || '';
  
  // Get the ACTUAL tailoring duration from the tailoring page (VectorShift processing time)
  // Priority: location.state (fresh navigation) > database stored value
  const tailoringDurationFromState = location.state?.tailoringDuration;
  const tailoringDurationFromDb = tailoringResults?.tailoring_duration_seconds;
  
  // Helper to format seconds to "Xm Ys" or "Xs"
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };
  
  // Resolve the tailoring duration for display
  // Use explicit null/undefined checks so 0 seconds is treated as valid
  const resolvedTailoringDuration = (tailoringDurationFromState != null && Number.isFinite(tailoringDurationFromState))
    ? formatDuration(tailoringDurationFromState)
    : (tailoringDurationFromDb != null && Number.isFinite(tailoringDurationFromDb))
      ? formatDuration(tailoringDurationFromDb)
      : null;
  
  // === TAILORING DURATION DEBUG ===
  console.log('=== TAILORING DURATION DEBUG ===');
  console.log('tailoringDurationFromState:', tailoringDurationFromState);
  console.log('tailoringDurationFromDb:', tailoringDurationFromDb);
  console.log('resolvedTailoringDuration:', resolvedTailoringDuration);
  console.log('=== END TAILORING DURATION DEBUG ===');
  
  console.log('=== FINAL VALUES FOR DISPLAY ===');
  console.log('companyName for display:', companyName);
  console.log('jobTitle for display:', jobTitle);
  console.log('tailoringResults.company_name:', tailoringResults?.company_name);
  console.log('tailoringResults.job_title:', tailoringResults?.job_title);
  console.log('Final_Resume_Text length:', tailoringResults?.Final_Resume_Text?.length || 0);
  console.log('Cover_Letter length:', tailoringResults?.Cover_Letter?.length || 0);

  // Copy to clipboard helper function
  const handleCopy = async (content: string, label: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast({
        title: `Copied ${label} to clipboard`,
        description: `${label} has been copied to your clipboard.`
      });
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy to clipboard. Please try again.',
        variant: 'destructive'
      });
    }
  };


// removed legacy raw view early return (moved below hooks)

  // Generate filename using VectorShift data - prefer Resume_JSON.Name if available
  // IMPORTANT: No fake fallback names. If we can't find a real name, we derive a stable identifier from run_id.
  const extractPersonName = (): string => {
    // Priority 1: Use Name from Resume_JSON directly
    if (tailoringResults?.Resume_JSON?.Name) {
      return tailoringResults.Resume_JSON.Name;
    }

    // Priority 2: Use Name from jsonResumeData (converted or directly set)
    if (jsonResumeData?.Name) {
      return jsonResumeData.Name;
    }

    // Priority 3: If Final_Resume_Text is actually a JSON object (not a string), get Name from it
    if (typeof tailoringResults?.Final_Resume_Text === 'object' && tailoringResults?.Final_Resume_Text?.Name) {
      return tailoringResults.Final_Resume_Text.Name;
    }

    // Priority 4: Parse from Final_Resume_Text if it's a string
    const content = tailoringResults?.Final_Resume_Text;
    if (typeof content === 'string' && content) {
      const lines = content.split('\n');
      for (const line of lines) {
        const trimmed = line.trim();
        if (
          trimmed &&
          !trimmed.includes('@') &&
          !trimmed.includes('http') &&
          !trimmed.includes('Phone') &&
          !trimmed.includes('Email') &&
          trimmed.length > 3 &&
          trimmed.length < 50
        ) {
          return trimmed;
        }
      }
    }

    // Last resort: derive from run_id (real, deterministic), otherwise empty string.
    if (tailoringResults?.run_id && typeof tailoringResults.run_id === 'string') {
      return `Run-${tailoringResults.run_id.slice(0, 8)}`;
    }

    return '';
  };

  const personName = extractPersonName();
  const currentDate = new Date().toLocaleString('en-US', { 
    month: 'short', 
    year: 'numeric' 
  });
  const jobTitlePart = jobTitle ? ` - ${jobTitle}` : '';
  const companyPart = companyName ? ` - ${companyName}` : '';
  const generatedFilename = `${personName}${jobTitlePart}${companyPart} - ${currentDate}.docx`;
  
  console.log('Generated filename:', generatedFilename);

  // === NORMALIZED CONTENT STRINGS ===
  // Convert all content fields to safe strings ONCE, so the rest of the UI never crashes
  // For resume: VectorShift may return JSON under Final_Resume_Text, so prefer Tailored_Resume_Plain_Text for string operations.
  const resumeText =
    typeof tailoringResults?.Final_Resume_Text === 'string'
      ? tailoringResults.Final_Resume_Text
      : safeString(tailoringResults?.Tailored_Resume_Plain_Text);
  
  // For letters: check if the main field IS the JSON object (VectorShift now returns JSON directly)
  // If Cover_Letter is a VectorShift JSON object, use it as Cover_Letter_JSON
  const coverLetterJSON = tailoringResults?.Cover_Letter_JSON || 
    (isVectorShiftLetterJSON(tailoringResults?.Cover_Letter) ? tailoringResults.Cover_Letter : null);
  const thankYouJSON = tailoringResults?.Thank_You_Letter_JSON || 
    (isVectorShiftLetterJSON(tailoringResults?.Thank_You_Letter) ? tailoringResults.Thank_You_Letter : null);
  const outreachJSON = tailoringResults?.Outreach_Email_JSON || 
    (isVectorShiftLetterJSON(tailoringResults?.Outreach_Letter) ? tailoringResults.Outreach_Letter : null);
  
  // LinkedIn JSON - VectorShift may return as LinkedIn_Profile (capital I) or Linkedin_Profile (lowercase i)
  const linkedinRaw = tailoringResults?.LinkedIn_Profile || tailoringResults?.Linkedin_Profile;
  const linkedinJSON = isVectorShiftLinkedInJSON(linkedinRaw) ? linkedinRaw : null;
  
  // Normalized text for documents array - NO FALLBACKS between different fields
  const coverLetterText = safeString(tailoringResults?.Cover_Letter_JSON) || safeString(tailoringResults?.Cover_Letter);
  const thankYouText = safeString(tailoringResults?.Thank_You_Letter_JSON) || safeString(tailoringResults?.Thank_You_Letter);
  const executiveBioText = safeString(tailoringResults?.Executive_Bio_JSON) || safeString(tailoringResults?.Executive_Bio);
  // Structured navy bio (new design). Old sessions carry prose and render the legacy path.
  const structuredBio = parseStructuredBio(tailoringResults?.Executive_Bio_JSON ?? tailoringResults?.Executive_Bio);
  // For LinkedIn, if it's a JSON object, stringify it for display or use the About section as preview
  const linkedinText = linkedinJSON 
    ? (linkedinJSON.LinkedIn_About || JSON.stringify(linkedinJSON, null, 2))
    : safeString(linkedinRaw);
  const outreachText = safeString(tailoringResults?.Outreach_Letter) || safeString(tailoringResults?.Outreach_Email_JSON);

  // Debug: log the actual types we received from VectorShift
  console.log('=== CONTENT TYPE DEBUG ===');
  console.log('Final_Resume_Text type:', typeof tailoringResults?.Final_Resume_Text);
  console.log('Cover_Letter type:', typeof tailoringResults?.Cover_Letter);
  console.log('Cover_Letter is VectorShift JSON:', isVectorShiftLetterJSON(tailoringResults?.Cover_Letter));
  console.log('coverLetterJSON resolved:', !!coverLetterJSON);
  console.log('Resume_JSON exists:', !!tailoringResults?.Resume_JSON);
  console.log('Normalized resumeText length:', resumeText.length);
  console.log('=== LINKEDIN DEBUG ===');
  console.log('LinkedIn_Profile (capital I) raw:', tailoringResults?.LinkedIn_Profile);
  console.log('Linkedin_Profile (lowercase i) raw:', tailoringResults?.Linkedin_Profile);
  console.log('linkedinRaw resolved:', !!linkedinRaw);
  console.log('isVectorShiftLinkedInJSON:', isVectorShiftLinkedInJSON(linkedinRaw));
  console.log('linkedinJSON resolved:', !!linkedinJSON);
  console.log('linkedinText length:', linkedinText?.length || 0);

  // Validate we have real content, not just placeholders
  // Also treat resume JSON (either Resume_JSON or Final_Resume_Text object) as valid content for templated view
  const hasResumeJSON = !!tailoringResults?.Resume_JSON || isResumeJsonLike(tailoringResults?.Final_Resume_Text);
  const hasRealContent = hasResumeJSON || (
    resumeText.length > 100 &&
    !resumeText.includes('[Company Name]') &&
    !resumeText.includes('[Job Title]')
  );
  
  console.log('Has real content (not placeholders):', hasRealContent);
  
  if (!hasRealContent) {
    console.error('WARNING: Content appears to contain placeholders or is too short!');
    console.error('Content preview:', safePreview(tailoringResults?.Final_Resume_Text, 200));
  }

  // Create documents array - use NORMALIZED string content to prevent .split/.substring crashes
  const documents: TailoredDocument[] = tailoringResults ? [
    {
      id: '1',
      type: 'resume',
      title: 'Tailored Resume',
      content: resumeText, // Use normalized string
      createdAt: new Date(),
      jobTitle: jobTitle,
      company: companyName,
      file_name: generatedFilename
    },
    ...(selectedDocuments.includes('cover-letter') && coverLetterText ? [{
      id: '2',
      type: 'cover-letter' as const,
      title: 'Cover Letter',
      content: coverLetterText, // Use normalized string
      createdAt: new Date(),
      jobTitle: jobTitle,
      company: companyName,
      file_name: generatedFilename.replace('.docx', ' - Cover Letter.docx')
    }] : []),
    ...(selectedDocuments.includes('thank-you-letter') && thankYouText ? [{
      id: '3',
      type: 'thank-you-letter' as const,
      title: 'Thank You Letter',
      content: thankYouText, // Use normalized string
      createdAt: new Date(),
      jobTitle: jobTitle,
      company: companyName,
      file_name: generatedFilename.replace('.docx', ' - Thank You Letter.docx')
    }] : []),
    ...(selectedDocuments.includes('executive-bio') && executiveBioText ? [{
      id: '4',
      type: 'executive-bio' as const,
      title: 'Executive Bio',
      content: executiveBioText, // Use normalized string
      createdAt: new Date(),
      jobTitle: jobTitle,
      company: companyName,
      file_name: generatedFilename.replace('.docx', ' - Executive Bio.docx')
    }] : []),
    ...(selectedDocuments.includes('linkedin-profile') && linkedinText ? [{
      id: '5',
      type: 'linkedin-profile' as const,
      title: 'LinkedIn Profile',
      content: linkedinText, // Use normalized string
      createdAt: new Date(),
      jobTitle: jobTitle,
      company: companyName,
      file_name: generatedFilename.replace('.docx', ' - LinkedIn Profile.docx')
    }] : []),
    // Outreach: accept BOTH canonical 'outreach-letter' (new) and legacy
    // 'outreach-email' (old saved sessions) for backwards compatibility.
    ...((selectedDocuments.includes('outreach-letter') || selectedDocuments.includes('outreach-email')) && outreachText ? [{
      id: '6',
      type: 'outreach-letter' as const,
      title: 'Outreach Letter',
      content: outreachText, // Use normalized string
      createdAt: new Date(),
      jobTitle: jobTitle,
      company: companyName,
      file_name: generatedFilename.replace('.docx', ' - Outreach Letter.docx')
    }] : [])
  ] : [];

  const [activeTab, setActiveTab] = useState(documents[0]?.id || '1');
  const currentDocument = documents.find(doc => doc.id === activeTab);

  // ============ AUTO-GENERATE DOCMOSIS WORD DOCUMENTS ============
  // When VectorShift results load, automatically call Docmosis to generate real Word documents
  useEffect(() => {
    // Skip if already attempted, no results, or already generated
    if (docmosisGenerationAttempted || !tailoringResults || Object.keys(generatedWordDocs).length > 0) {
      return;
    }
    // Companion letters are generating in the background (stage 8) — wait for
    // them to settle so the letter documents render in the same pass. If they
    // fail, lettersSettled flips anyway and we proceed with the resume.
    if ((location.state as any)?.letterGenInputs && !lettersSettled.current) {
      console.log('⏳ Waiting for background companion letters before generating documents…');
      return;
    }

    // Check if we have resume JSON to work with — flatten Claude unified format if needed
    const rawResumeJson = tailoringResults?.Resume_JSON || 
      (isResumeJsonLike(tailoringResults?.Final_Resume_Text) ? tailoringResults.Final_Resume_Text : null) ||
      jsonResumeData;
    const resumeJsonData = flattenClaudeUnifiedJson(rawResumeJson);

    if (!resumeJsonData) {
      console.log('⏳ Waiting for resume JSON data before generating Docmosis documents...');
      return;
    }

    setDocmosisGenerationAttempted(true);
    
    const autoGenerateDocmosisDocuments = async () => {
      console.log('=== AUTO-GENERATING DOCMOSIS WORD DOCUMENTS ===');
      setIsGeneratingDocmosis(true);
      setDocmosisGenerationError(null);

      const newGeneratedDocs: typeof generatedWordDocs = {};
      // Set true the moment the resume generator itself reports a SPECIFIC
      // reason, so the generic fallback below (which fires whenever nothing
      // generated, for ANY reason) never overwrites it with a vague message.
      let specificDocErrorCaptured = false;

      // Helper to upload base64 to storage and get public URL
      const uploadToStorage = async (filename: string, base64Data: string): Promise<string | undefined> => {
        try {
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          });

          const safeFileName = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
          const timestamp = Date.now();
          const storagePath = `generated_${timestamp}_${safeFileName}`;

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('word-documents')
            .upload(storagePath, blob, { cacheControl: '3600', upsert: false });

          if (uploadError) {
            console.error('Upload error:', uploadError);
            return undefined;
          }

          const { data: urlData } = supabase.storage.from('word-documents').getPublicUrl(storagePath);
          return urlData?.publicUrl;
        } catch (error) {
          console.error('Failed to upload to storage:', error);
          return undefined;
        }
      };

      try {
        // 1. Generate Resume via Docmosis
        if (resumeJsonData) {
          console.log('📄 Generating resume via Docmosis with template:', selectedDocmosisTemplate);
          try {
            const resumeResponse = await BulkResumeService.generateBulkResumes(resumeJsonData, selectedDocmosisTemplate);
            if (resumeResponse.success && resumeResponse.files.length > 0) {
              const file = resumeResponse.files[0];
              const publicUrl = await uploadToStorage(file.filename, file.data);
              newGeneratedDocs.resume = { ...file, publicUrl };
              console.log('✅ Resume Word document generated:', file.filename, 'URL:', publicUrl);
            } else {
              // success:true with zero files (or success:false that didn't
              // throw) is not a throw, so it fell through silently before —
              // surface it with whatever the server actually said.
              const reason = (resumeResponse as any)?.error || `server returned success=${resumeResponse.success}, ${resumeResponse.files?.length ?? 0} file(s)`;
              console.error('❌ Resume generation returned no usable file:', reason);
              setDocmosisGenerationError(`Document generation failed: ${reason}`);
              specificDocErrorCaptured = true;
            }
          } catch (error: any) {
            // The real reason the server failed, not a generic placeholder.
            console.error('❌ Failed to generate resume via Docmosis:', error);
            setDocmosisGenerationError(`Document generation failed: ${error?.message || String(error)}`);
            specificDocErrorCaptured = true;
          }
        }

        // 2. Generate Cover Letter via Docmosis (only if user selected it)
        const coverJSON = tailoringResults?.Cover_Letter_JSON ||
          (isVectorShiftLetterJSON(tailoringResults?.Cover_Letter) ? tailoringResults.Cover_Letter : null);

        if (coverJSON?.Body && wasSelected('cover-letter', 'coverLetter')) {
          console.log('📄 Generating cover letter via Docmosis...');
          try {
            const contactName = resumeJsonData?.Name || personName || '';
            const [city, state] = (resumeJsonData?.City && resumeJsonData?.State) 
              ? [resumeJsonData.City, resumeJsonData.State]
              : (resumeJsonData?.CityST || '').split(',').map((s: string) => s.trim());

            const letterRequest = {
              letters: [{
                headerInfo: {
                  fullName: contactName,
                  phoneNumber: resumeJsonData?.Phone || '',
                  email: resumeJsonData?.Email || '',
                  cityST: `${city || ''}, ${state || ''}`,
                  linkedin: resumeJsonData?.LinkedIn || '',
                  companyName: companyName || coverJSON?.CompanyName || '',
                  recipientName: coverJSON?.RecipientName || '',
                  jobTitle: jobTitle || coverJSON?.JobTitle || ''
                },
                coverLetterBody: coverJSON.Body
              }],
              letterTypes: { coverLetter: true, thankYouLetter: false, outreachLetter: false },
              templateId: selectedDocmosisTemplate
            };

            const letterResponse = await BulkLetterService.generateBulkLetters(letterRequest);
            if (letterResponse.success && letterResponse.files.length > 0) {
              const file = letterResponse.files[0];
              const publicUrl = await uploadToStorage(file.filename, file.data);
              newGeneratedDocs.coverLetter = { ...file, publicUrl };
              console.log('✅ Cover letter generated:', file.filename, 'URL:', publicUrl);
            }
          } catch (error) {
            console.error('❌ Failed to generate cover letter via Docmosis:', error);
          }
        }

        // 3. Generate Thank You Letter via Docmosis (only if user selected it)
        const thankYouJSONData = tailoringResults?.Thank_You_Letter_JSON ||
          (isVectorShiftLetterJSON(tailoringResults?.Thank_You_Letter) ? tailoringResults.Thank_You_Letter : null);

        if (thankYouJSONData?.Body && wasSelected('thank-you-letter', 'thankYouLetter')) {
          console.log('📄 Generating thank you letter via Docmosis...');
          try {
            const contactName = resumeJsonData?.Name || personName || '';
            const [city, state] = (resumeJsonData?.City && resumeJsonData?.State) 
              ? [resumeJsonData.City, resumeJsonData.State]
              : (resumeJsonData?.CityST || '').split(',').map((s: string) => s.trim());

            const letterRequest = {
              letters: [{
                headerInfo: {
                  fullName: contactName,
                  phoneNumber: resumeJsonData?.Phone || '',
                  email: resumeJsonData?.Email || '',
                  cityST: `${city || ''}, ${state || ''}`,
                  linkedin: resumeJsonData?.LinkedIn || '',
                  companyName: companyName || '',
                  recipientName: thankYouJSONData?.RecipientName || '',
                  jobTitle: jobTitle || ''
                },
                thankYouLetterBody: thankYouJSONData.Body
              }],
              letterTypes: { coverLetter: false, thankYouLetter: true, outreachLetter: false },
              templateId: selectedDocmosisTemplate
            };

            const letterResponse = await BulkLetterService.generateBulkLetters(letterRequest);
            if (letterResponse.success && letterResponse.files.length > 0) {
              const file = letterResponse.files[0];
              const publicUrl = await uploadToStorage(file.filename, file.data);
              newGeneratedDocs.thankYouLetter = { ...file, publicUrl };
              console.log('✅ Thank you letter generated:', file.filename, 'URL:', publicUrl);
            }
          } catch (error) {
            console.error('❌ Failed to generate thank you letter via Docmosis:', error);
          }
        }

        // 4. Generate Outreach Letter via Docmosis (only if user selected it)
        const outreachJSONData = tailoringResults?.Outreach_Email_JSON ||
          (isVectorShiftLetterJSON(tailoringResults?.Outreach_Letter) ? tailoringResults.Outreach_Letter : null);

        if (outreachJSONData?.Body && wasSelected('outreach-letter', 'outreach-email', 'outreachLetter')) {
          console.log('📄 Generating outreach letter via Docmosis...');
          try {
            const contactName = resumeJsonData?.Name || personName || '';
            const [city, state] = (resumeJsonData?.City && resumeJsonData?.State) 
              ? [resumeJsonData.City, resumeJsonData.State]
              : (resumeJsonData?.CityST || '').split(',').map((s: string) => s.trim());

            const letterRequest = {
              letters: [{
                headerInfo: {
                  fullName: contactName,
                  phoneNumber: resumeJsonData?.Phone || '',
                  email: resumeJsonData?.Email || '',
                  cityST: `${city || ''}, ${state || ''}`,
                  linkedin: resumeJsonData?.LinkedIn || '',
                  companyName: companyName || '',
                  recipientName: outreachJSONData?.RecipientName || '',
                  jobTitle: jobTitle || ''
                },
                outreachLetterBody: outreachJSONData.Body
              }],
              letterTypes: { coverLetter: false, thankYouLetter: false, outreachLetter: true },
              templateId: selectedDocmosisTemplate
            };

            const letterResponse = await BulkLetterService.generateBulkLetters(letterRequest);
            if (letterResponse.success && letterResponse.files.length > 0) {
              const file = letterResponse.files[0];
              const publicUrl = await uploadToStorage(file.filename, file.data);
              newGeneratedDocs.outreachLetter = { ...file, publicUrl };
              console.log('✅ Outreach letter generated:', file.filename, 'URL:', publicUrl);
            }
          } catch (error) {
            console.error('❌ Failed to generate outreach letter via Docmosis:', error);
          }
        }

        // 5. Generate LinkedIn document via Docmosis (only if user selected it)
        // Check both LinkedIn_Profile (capital I) and Linkedin_Profile (lowercase i)
        const linkedinData = tailoringResults?.LinkedIn_Profile || tailoringResults?.Linkedin_Profile;
        if (linkedinData && typeof linkedinData === 'object' && isVectorShiftLinkedInJSON(linkedinData) && wasSelected('linkedin-profile', 'linkedinProfile')) {
          console.log('📄 Generating LinkedIn document via Docmosis...', linkedinData);
          try {
            const { data, error } = await supabase.functions.invoke('generate-linkedin-document', {
              body: linkedinData
            });
            
            if (error) {
              console.error('❌ LinkedIn generation error:', error);
            } else if (data?.success && data?.files?.length > 0) {
              const file = data.files[0];
              const publicUrl = await uploadToStorage(file.filename, file.data);
              newGeneratedDocs.linkedin = {
                filename: file.filename,
                data: file.data,
                size: file.size,
                publicUrl
              };
              console.log('✅ LinkedIn document generated:', file.filename, 'URL:', publicUrl);
            } else {
              console.error('❌ LinkedIn generation returned no files:', data);
            }
          } catch (error) {
            console.error('❌ Failed to generate LinkedIn via Docmosis:', error);
          }
        }

        // 6. Solution Asset Document (Deep Tailor only — needs pain points
        // from the Tailoring Report). Renders via Docmosis cover-letter
        // template family matching the resume.
        if (wasSelected('solution-asset-document') && tailoringReportPainPoints) {
          console.log('📄 Generating Solution Asset…');
          try {
            const candidateName = resumeJsonData?.Name || personName || '';
            const [city, state] = (resumeJsonData?.City && resumeJsonData?.State)
              ? [resumeJsonData.City, resumeJsonData.State]
              : (resumeJsonData?.CityST || '').split(',').map((s: string) => s.trim());
            const hiringManagerName = Array.isArray(hiringManagerCandidates) && hiringManagerCandidates.length > 0
              ? hiringManagerCandidates[0]?.name || ''
              : '';
            const { data, error } = await supabase.functions.invoke('generate-solution-asset', {
              body: {
                candidateName,
                candidateEmail: resumeJsonData?.Email || '',
                candidatePhone: resumeJsonData?.Phone || '',
                cityST: `${city || ''}${state ? ', ' + state : ''}`,
                linkedin: resumeJsonData?.LinkedIn || '',
                resumeText: typeof originalResumeText === 'string' ? originalResumeText : safeString(tailoringResults?.Final_Resume_Text),
                companyName: companyName || '',
                jobTitle: jobTitle || '',
                painPoints: tailoringReportPainPoints,
                hiringManagerName,
                templateId: selectedDocmosisTemplate,
              },
            });
            if (error) throw error;
            if (data?.success && data?.files?.length > 0) {
              const file = data.files[0];
              const publicUrl = await uploadToStorage(file.filename, file.data);
              newGeneratedDocs.solutionAsset = { ...file, publicUrl };
              console.log('✅ Solution Asset generated:', file.filename);
            } else {
              console.error('❌ Solution Asset returned no files:', data);
            }
          } catch (err) {
            console.error('❌ Failed to generate Solution Asset:', err);
          }
        }

        // 7. Hiring Manager Contact Report (Deep Tailor only — needs Apollo
        // contacts + pain points). Renders via Docmosis cover-letter template.
        if (wasSelected('hiring-manager-contact-report')) {
          console.log('📄 Generating Hiring Manager Contact Report…');
          try {
            const candidateName = resumeJsonData?.Name || personName || '';
            const [city, state] = (resumeJsonData?.City && resumeJsonData?.State)
              ? [resumeJsonData.City, resumeJsonData.State]
              : (resumeJsonData?.CityST || '').split(',').map((s: string) => s.trim());
            const { data, error } = await supabase.functions.invoke('generate-hiring-manager-report', {
              body: {
                candidateName,
                candidateEmail: resumeJsonData?.Email || '',
                candidatePhone: resumeJsonData?.Phone || '',
                cityST: `${city || ''}${state ? ', ' + state : ''}`,
                linkedin: resumeJsonData?.LinkedIn || '',
                resumeText: typeof originalResumeText === 'string' ? originalResumeText : safeString(tailoringResults?.Final_Resume_Text),
                companyName: companyName || '',
                jobTitle: jobTitle || '',
                painPoints: tailoringReportPainPoints || '',
                apolloContacts: hiringManagerCandidates || [],
                templateId: selectedDocmosisTemplate,
              },
            });
            if (error) throw error;
            if (data?.success && data?.files?.length > 0) {
              const file = data.files[0];
              const publicUrl = await uploadToStorage(file.filename, file.data);
              newGeneratedDocs.hiringManagerReport = { ...file, publicUrl };
              console.log('✅ HM Report generated:', file.filename);
            } else {
              console.error('❌ HM Report returned no files:', data);
            }
          } catch (err) {
            console.error('❌ Failed to generate HM Report:', err);
          }
        }

        // Update state with all generated documents
        if (Object.keys(newGeneratedDocs).length > 0) {
          setGeneratedWordDocs(newGeneratedDocs);
          
          // Set active doc type based on what the USER SELECTED - NO FALLBACKS
          // Map selectedDocuments keys to activeDocType keys
          const docKeyMap: Record<string, 'resume' | 'coverLetter' | 'thankYouLetter' | 'outreachLetter' | 'linkedin'> = {
            'resume': 'resume',
            'cover-letter': 'coverLetter',
            'thank-you-letter': 'thankYouLetter',
            'outreach-email': 'outreachLetter',
            'linkedin-profile': 'linkedin'
          };
          
          // Find the first document the user selected that was also generated
          let selectedActiveDoc: 'resume' | 'coverLetter' | 'thankYouLetter' | 'outreachLetter' | 'linkedin' | null = null;
          for (const selectedKey of selectedDocuments) {
            const activeKey = docKeyMap[selectedKey];
            if (activeKey && newGeneratedDocs[activeKey]) {
              selectedActiveDoc = activeKey;
              break;
            }
          }
          
          // Only set activeDocType if we found a match - otherwise keep default
          if (selectedActiveDoc) {
            setActiveDocType(selectedActiveDoc);
          }
          console.log('📋 Active doc set to:', selectedActiveDoc, 'based on selectedDocuments:', selectedDocuments);
          
          toast({
            title: 'Word Documents Ready',
            description: `Generated ${Object.keys(newGeneratedDocs).length} professional Word document(s).`
          });
        } else if (!specificDocErrorCaptured) {
          setDocmosisGenerationError('No documents were generated. Resume JSON may be missing required fields.');
        }

      } catch (error: any) {
        console.error('❌ Error auto-generating Docmosis documents:', error);
        setDocmosisGenerationError(error.message || 'Failed to generate Word documents');
        toast({
          title: 'Document Generation Failed',
          description: 'Could not generate Word documents.',
          variant: 'destructive'
        });
      } finally {
        setIsGeneratingDocmosis(false);
      }
    };

    autoGenerateDocmosisDocuments();
  }, [tailoringResults, jsonResumeData, docmosisGenerationAttempted, generatedWordDocs, companyName, jobTitle, personName, lettersGenerating]);

  // Generate clean filename for downloads (matching WordDocumentViewer format)
  const generateCleanFilename = (docType: string, format: 'docx' | 'pdf' = 'docx'): string => {
    const docTypeLabel = docType === 'coverLetter' ? 'Cover Letter' 
      : docType === 'thankYouLetter' ? 'Thank You Letter'
      : docType === 'outreachLetter' ? 'Outreach Letter'
      : docType === 'linkedin' ? 'LinkedIn Profile'
      : 'Resume';
    
    let cleanName = '';
    if (personName) {
      cleanName = personName;
      if (jobTitle && companyName) {
        cleanName += ` - ${docTypeLabel} - ${jobTitle} - ${companyName} - ${currentDate}`;
      } else if (companyName) {
        cleanName += ` - ${docTypeLabel} - ${companyName} - ${currentDate}`;
      } else if (jobTitle) {
        cleanName += ` - ${docTypeLabel} - ${jobTitle} - ${currentDate}`;
      } else {
        cleanName += ` - ${docTypeLabel} - ${currentDate}`;
      }
    } else {
      cleanName = `${docTypeLabel} - ${currentDate}`;
    }
    
    // Clean the filename but preserve spaces
    const sanitized = cleanName.replace(/[^a-zA-Z0-9\s\-_.]/g, '').replace(/\s+/g, ' ').trim();
    return `${sanitized}.${format}`;
  };

  // Helper function to download generated Word document
  // The saved document's bytes. A refreshed or re-opened session only holds
  // the saved file's link (data is empty), so fetch the file itself: decoding
  // the empty data produced a blank Word file (Oct 25 2026).
  const getSavedDocBlob = async (docType: keyof typeof generatedWordDocs): Promise<Blob> => {
    const doc: any = generatedWordDocs[docType];
    if (!doc) throw new Error('no document');
    if (doc.data) {
      const bin = atob(doc.data);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    }
    if (!doc.publicUrl) throw new Error('no saved file');
    const res = await fetch(doc.publicUrl, { cache: 'no-store' });
    if (!res.ok) throw new Error(`saved file ${res.status}`);
    const blob = await res.blob();
    if (blob.size < 100) throw new Error('saved file is empty');
    return blob;
  };

  const handleDownloadGeneratedDoc = async (docType: keyof typeof generatedWordDocs) => {
    const doc = generatedWordDocs[docType];
    if (!doc) return;
    const cleanFilename = generateCleanFilename(docType, 'docx');
    try {
      const blob = await getSavedDocBlob(docType);
      BulkResumeService.downloadBlob(blob, cleanFilename);
      toast({ title: 'Download Started', description: `Downloading ${cleanFilename}` });
    } catch (e) {
      console.error('Word download failed:', e);
      toast({ title: "Sorry, we couldn't download your document right now", description: 'Please try again in a few minutes.', variant: 'destructive' });
    }
  };

  // Function to regenerate resume with a different template.  Accepts both
  // the 9 hardcoded Docmosis template ids AND custom-template UUIDs published
  // from the Resume Template Wizard.
  const handleRegenerateWithTemplate = async (newTemplate: string) => {
    if (newTemplate === selectedDocmosisTemplate && generatedWordDocs.resume) {
      const label = isCustomTemplateId(newTemplate)
        ? (customTemplates.find(t => t.id === newTemplate)?.template_name || 'this template')
        : docmosisTemplateLabel(newTemplate);
      toast({
        title: 'Already using this template',
        description: `Documents are already generated with ${label}.`,
      });
      return;
    }

    setSelectedDocmosisTemplate(newTemplate);
    setIsGeneratingDocmosis(true);
    setDocmosisGenerationError(null);

    const rawResumeJson2 = tailoringResults?.Resume_JSON ||
      (isResumeJsonLike(tailoringResults?.Final_Resume_Text) ? tailoringResults.Final_Resume_Text : null) ||
      jsonResumeData;
    const resumeJsonData = flattenClaudeUnifiedJson(rawResumeJson2);

    if (!resumeJsonData) {
      toast({
        title: 'No resume data',
        description: 'Resume JSON data is not available for regeneration.',
        variant: 'destructive'
      });
      setIsGeneratingDocmosis(false);
      return;
    }

    // ─────────────────────────────────────────────────────────────
    // CUSTOM TEMPLATE PATH (Wizard-published, UUID id)
    // Goes through the WIZARD's render endpoint on Railway instead of
    // the Supabase edge function, because that project hit its
    // edge-function quota and can't deploy new code. The Wizard has its
    // own DOCMOSIS_API_KEY env var and calls Docmosis Tornado directly.
    // ─────────────────────────────────────────────────────────────
    if (isCustomTemplateId(newTemplate)) {
      const WIZARD_RENDER_URL = "https://wizard-production-de29.up.railway.app/api/render-resume";
      try {
        const custom = customTemplates.find(t => t.id === newTemplate);
        const docmosisFilename =
          (custom as any)?.style_metadata?.docmosis_filename
          || custom?.original_filename
          || `${(custom?.template_name || 'wizard').toLowerCase().replace(/[^a-z0-9]+/g,'-')}.docx`;
        const safeOutput = `${(resumeJsonData?.Name || 'resume').toString().replace(/[^A-Za-z0-9._ -]+/g,'_')}_${custom?.template_name || 'custom'}`;
        const res = await fetch(WIZARD_RENDER_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            docmosis_filename: docmosisFilename,
            resume_data: resumeJsonData,
            output_format: 'docx',
            output_name: safeOutput,
          }),
        });
        const body = await res.json();
        if (!res.ok || !body.success || !body.files?.length) {
          throw new Error(body.error || `Wizard render failed (${res.status})`);
        }
        const file = body.files[0];
        const safeFileName = file.filename.replace(/[^a-zA-Z0-9._-]/g, '_');
        const storagePath = `generated_${Date.now()}_${safeFileName}`;
        const binaryString = atob(file.data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        const { error: uploadError } = await supabase.storage
          .from('word-documents')
          .upload(storagePath, blob, { cacheControl: '3600', upsert: false });
        let publicUrl: string | undefined;
        if (!uploadError) {
          const { data: urlData } = supabase.storage.from('word-documents').getPublicUrl(storagePath);
          publicUrl = urlData?.publicUrl;
        }
        setGeneratedWordDocs(prev => ({
          ...prev,
          resume: { ...file, publicUrl },
        }));
        toast({
          title: 'Resume regenerated',
          description: `Using ${custom?.template_name || 'custom template'} — published via the Wizard.`,
        });
      } catch (err: any) {
        console.error('Custom-template regen failed:', err);
        const msg = err?.message || 'Custom template generation failed';
        setDocmosisGenerationError(msg);
        toast({
          title: 'Generation failed',
          description: msg.toLowerCase().includes('not found') || msg.includes('404')
            ? 'Docmosis could not find that template. Make sure the .docx has been uploaded to Docmosis with the exact filename shown in the admin Templates page (Docmosis name column).'
            : msg,
          variant: 'destructive',
        });
      } finally {
        setIsGeneratingDocmosis(false);
      }
      return;
    }

    // Helper to upload base64 to storage and get public URL
    const uploadToStorage = async (filename: string, base64Data: string): Promise<string | undefined> => {
      try {
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });

        const safeFileName = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
        const timestamp = Date.now();
        const storagePath = `generated_${timestamp}_${safeFileName}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('word-documents')
          .upload(storagePath, blob, { cacheControl: '3600', upsert: false });

        if (uploadError) {
          console.error('Upload error:', uploadError);
          return undefined;
        }

        const { data: urlData } = supabase.storage.from('word-documents').getPublicUrl(storagePath);
        return urlData?.publicUrl;
      } catch (error) {
        console.error('Failed to upload to storage:', error);
        return undefined;
      }
    };

    try {
      const newGeneratedDocs: typeof generatedWordDocs = {};
      const templateLabel = docmosisTemplateLabel(newTemplate);
      
      // 1. Regenerate Resume
      console.log(`📄 Regenerating resume with ${templateLabel}...`);
      const resumeResponse = await BulkResumeService.generateBulkResumes(resumeJsonData, newTemplate);
      if (resumeResponse.success && resumeResponse.files.length > 0) {
        const file = resumeResponse.files[0];
        const publicUrl = await uploadToStorage(file.filename, file.data);
        newGeneratedDocs.resume = { ...file, publicUrl };
        console.log('✅ Resume regenerated:', file.filename);
      }

      // 2. Regenerate Cover Letter (only if user selected it)
      const coverJSON = tailoringResults?.Cover_Letter_JSON ||
        (isVectorShiftLetterJSON(tailoringResults?.Cover_Letter) ? tailoringResults.Cover_Letter : null);

      if (coverJSON?.Body && wasSelected('cover-letter', 'coverLetter')) {
        console.log(`📄 Regenerating cover letter with ${templateLabel}...`);
        try {
          const contactName = resumeJsonData?.Name || personName || '';
          const [city, state] = (resumeJsonData?.City && resumeJsonData?.State) 
            ? [resumeJsonData.City, resumeJsonData.State]
            : (resumeJsonData?.CityST || '').split(',').map((s: string) => s.trim());

          const letterRequest = {
            letters: [{
              headerInfo: {
                fullName: contactName,
                phoneNumber: resumeJsonData?.Phone || '',
                email: resumeJsonData?.Email || '',
                cityST: `${city || ''}, ${state || ''}`,
                linkedin: resumeJsonData?.LinkedIn || '',
                companyName: companyName || coverJSON?.CompanyName || '',
                recipientName: coverJSON?.RecipientName || '',
                jobTitle: jobTitle || coverJSON?.JobTitle || ''
              },
              coverLetterBody: coverJSON.Body
            }],
            letterTypes: { coverLetter: true, thankYouLetter: false, outreachLetter: false },
            templateId: newTemplate
          };

          const letterResponse = await BulkLetterService.generateBulkLetters(letterRequest);
          if (letterResponse.success && letterResponse.files.length > 0) {
            const file = letterResponse.files[0];
            const publicUrl = await uploadToStorage(file.filename, file.data);
            newGeneratedDocs.coverLetter = { ...file, publicUrl };
            console.log('✅ Cover letter regenerated:', file.filename);
          }
        } catch (error) {
          console.error('❌ Failed to regenerate cover letter:', error);
        }
      }

      // 3. Regenerate Thank You Letter (only if user selected it)
      const thankYouJSONData = tailoringResults?.Thank_You_Letter_JSON ||
        (isVectorShiftLetterJSON(tailoringResults?.Thank_You_Letter) ? tailoringResults.Thank_You_Letter : null);

      if (thankYouJSONData?.Body && wasSelected('thank-you-letter', 'thankYouLetter')) {
        console.log(`📄 Regenerating thank you letter with ${templateLabel}...`);
        try {
          const contactName = resumeJsonData?.Name || personName || '';
          const [city, state] = (resumeJsonData?.City && resumeJsonData?.State) 
            ? [resumeJsonData.City, resumeJsonData.State]
            : (resumeJsonData?.CityST || '').split(',').map((s: string) => s.trim());

          const letterRequest = {
            letters: [{
              headerInfo: {
                fullName: contactName,
                phoneNumber: resumeJsonData?.Phone || '',
                email: resumeJsonData?.Email || '',
                cityST: `${city || ''}, ${state || ''}`,
                linkedin: resumeJsonData?.LinkedIn || '',
                companyName: companyName || '',
                recipientName: thankYouJSONData?.RecipientName || '',
                jobTitle: jobTitle || ''
              },
              thankYouLetterBody: thankYouJSONData.Body
            }],
            letterTypes: { coverLetter: false, thankYouLetter: true, outreachLetter: false },
            templateId: newTemplate
          };

          const letterResponse = await BulkLetterService.generateBulkLetters(letterRequest);
          if (letterResponse.success && letterResponse.files.length > 0) {
            const file = letterResponse.files[0];
            const publicUrl = await uploadToStorage(file.filename, file.data);
            newGeneratedDocs.thankYouLetter = { ...file, publicUrl };
            console.log('✅ Thank you letter regenerated:', file.filename);
          }
        } catch (error) {
          console.error('❌ Failed to regenerate thank you letter:', error);
        }
      }

      // 4. Regenerate Outreach Letter (only if user selected it)
      const outreachJSONData = tailoringResults?.Outreach_Email_JSON ||
        (isVectorShiftLetterJSON(tailoringResults?.Outreach_Letter) ? tailoringResults.Outreach_Letter : null);

      if (outreachJSONData?.Body && wasSelected('outreach-letter', 'outreach-email', 'outreachLetter')) {
        console.log(`📄 Regenerating outreach letter with ${templateLabel}...`);
        try {
          const contactName = resumeJsonData?.Name || personName || '';
          const [city, state] = (resumeJsonData?.City && resumeJsonData?.State) 
            ? [resumeJsonData.City, resumeJsonData.State]
            : (resumeJsonData?.CityST || '').split(',').map((s: string) => s.trim());

          const letterRequest = {
            letters: [{
              headerInfo: {
                fullName: contactName,
                phoneNumber: resumeJsonData?.Phone || '',
                email: resumeJsonData?.Email || '',
                cityST: `${city || ''}, ${state || ''}`,
                linkedin: resumeJsonData?.LinkedIn || '',
                companyName: companyName || '',
                recipientName: outreachJSONData?.RecipientName || '',
                jobTitle: jobTitle || ''
              },
              outreachLetterBody: outreachJSONData.Body
            }],
            letterTypes: { coverLetter: false, thankYouLetter: false, outreachLetter: true },
            templateId: newTemplate
          };

          const letterResponse = await BulkLetterService.generateBulkLetters(letterRequest);
          if (letterResponse.success && letterResponse.files.length > 0) {
            const file = letterResponse.files[0];
            const publicUrl = await uploadToStorage(file.filename, file.data);
            newGeneratedDocs.outreachLetter = { ...file, publicUrl };
            console.log('✅ Outreach letter regenerated:', file.filename);
          }
        } catch (error) {
          console.error('❌ Failed to regenerate outreach letter:', error);
        }
      }

      // Letters not rebuilt above (a reopened session does not hold their text)
      // keep their words and just take the new colour.
      for (const key of ['coverLetter', 'thankYouLetter', 'outreachLetter'] as const) {
        const saved = (generatedWordDocs as any)[key];
        if ((newGeneratedDocs as any)[key] || !saved?.publicUrl || !newTemplate.startsWith('rdr-blue-theme')) continue;
        const { data: rc, error: rcErr } = await supabase.functions.invoke('generate-bulk-letters', {
          body: { action: 'recolor-saved', docxUrl: saved.publicUrl, templateId: newTemplate },
        });
        if (rcErr || !rc?.success) { console.error('Letter recolour failed:', key, rcErr ?? rc?.error); continue; }
        const publicUrl = await uploadToStorage(saved.filename, rc.data);
        (newGeneratedDocs as any)[key] = { ...saved, data: rc.data, publicUrl };
      }

      // Update state with all regenerated documents
      setGeneratedWordDocs(prev => ({
        ...prev,
        ...newGeneratedDocs
      }));
      
      const docCount = Object.keys(newGeneratedDocs).length;
      toast({
        title: 'Documents Regenerated',
        description: `Successfully switched ${docCount} document(s) to ${templateLabel}.`
      });
      
    } catch (error: any) {
      console.error('❌ Failed to regenerate documents:', error);
      setDocmosisGenerationError(error.message || 'Failed to regenerate documents');
      toast({
        title: 'Regeneration Failed',
        description: error.message || 'Could not regenerate documents with new template.',
        variant: 'destructive'
      });
    } finally {
      setIsGeneratingDocmosis(false);
    }
  };

  // ── Perfect Fit ──────────────────────────────────────────────────────────
  // Measures every Career Highlight (target: 2 lines) and experience bullet
  // (target: 1 line) against the template's font metrics, sends ONLY the
  // overflowing lines to Claude with a strict character budget, writes the
  // trimmed lines back into the Resume_JSON, and re-renders the document.
  const handlePerfectFit = async () => {
    if (isOriginalFormat) {
      toast({ title: 'Perfect Fit is for the RDR Blue design', description: 'Switch the template to RDR Blue to run line fitting.' });
      return;
    }
    const rawJson = tailoringResults?.Resume_JSON ||
      (isResumeJsonLike(tailoringResults?.Final_Resume_Text) ? tailoringResults.Final_Resume_Text : null) ||
      jsonResumeData;
    const flat = flattenClaudeUnifiedJson(rawJson);
    if (!flat?.CareerHighlights && !flat?.ProfessionalExperience) {
      toast({ title: 'No resume data', description: 'Resume JSON is not available to fit.', variant: 'destructive' });
      return;
    }

    setIsFitting(true);
    try {
      const { findOverflows, applyFits } = await import('@/lib/resumeFit');
      const overflows = findOverflows(flat);
      if (overflows.length === 0) {
        toast({ title: 'Already fits', description: 'Every highlight is within 2 lines and every bullet within 1 line.' });
        return;
      }

      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error('You are not signed in.');

      const FIT_URL = 'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/tailor-resume-claude-jd';
      const FIT_APIKEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I';
      const res = await fetch(FIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: FIT_APIKEY },
        body: JSON.stringify({ stage: 5, items: overflows.map(({ id, text, max_chars }) => ({ id, text, max_chars })), ...ledgerRunFields() }),
      });
      if (!res.ok) throw new Error(`Fit failed — HTTP ${res.status}: ${(await res.text()).slice(0, 200)}`);
      const j = await res.json();
      if (!j?.success || !Array.isArray(j.items)) throw new Error(j?.error || 'Fit failed');

      const newFlat = applyFits(flat, j.items);

      // Record exactly what was edited so the user can open it.
      setFitChanges((j.items as Array<{ id: string; text: string }>).flatMap((f) => {
        const src = overflows.find((o) => o.id === f.id);
        return src && f.text?.trim() ? [{ label: src.label, before: src.text, after: f.text.trim() }] : [];
      }));
      setFitEngine('Perfect Fit');

      // Persist the trimmed lines into the nested Resume_JSON so template
      // switches and re-saves keep the fitted text.
      if (rawJson?.documents) {
        const nested = structuredClone(rawJson);
        const doc = nested.documents.find((d: any) => d.documentType === 'resume');
        if (doc?.data) {
          doc.data.CareerHighlights = newFlat.CareerHighlights;
          doc.data.ProfessionalExperience = newFlat.ProfessionalExperience;
        }
        setTailoringResults((prev: any) => ({ ...prev, Resume_JSON: nested }));
      } else {
        setJsonResumeData(newFlat);
        // Keep Resume_JSON in step: template switches rebuild from it.
        setTailoringResults((prev: any) => (prev?.Resume_JSON ? { ...prev, Resume_JSON: newFlat } : prev));
      }

      // Re-render the resume with the current template.
      const resumeResponse = await BulkResumeService.generateBulkResumes(newFlat, selectedDocmosisTemplate);
      if (!resumeResponse.success || resumeResponse.files.length === 0) {
        throw new Error('Document re-render failed after fitting.');
      }
      const file = resumeResponse.files[0];
      const binaryString = atob(file.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const storagePath = `generated_${Date.now()}_${file.filename.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error: uploadError } = await supabase.storage
        .from('word-documents')
        .upload(storagePath, blob, { cacheControl: '3600', upsert: false });
      let publicUrl: string | undefined;
      if (!uploadError) {
        publicUrl = supabase.storage.from('word-documents').getPublicUrl(storagePath).data?.publicUrl;
      }
      setGeneratedWordDocs(prev => ({ ...prev, resume: { ...file, publicUrl } }));

      toast({
        title: 'Perfect Fit applied',
        description: `Trimmed ${j.items.length} line${j.items.length === 1 ? '' : 's'} to fit. Click "View Edits" to see exactly what changed.`,
      });
    } catch (err: any) {
      console.error('Perfect Fit failed:', err);
      toast({ title: 'Perfect Fit failed', description: err?.message || 'Could not fit the document.', variant: 'destructive' });
    } finally {
      setIsFitting(false);
    }
  };

  // ── Layout QA ────────────────────────────────────────────────────────────
  // Second fitting engine, built on the Resume Layout QA skill: weighted
  // character-width measurement calibrated for Aptos 10.5pt (proportional font
  // — "W" is ~3x wider than "i"), prioritized shortening techniques, and a
  // verify-and-iterate loop: every returned line is re-measured against the
  // calibrated thresholds (PE bullet ≤ 74.0, highlight ≤ 137.0) and failures
  // are re-requested with a tighter target, up to 3 rounds.
  const handleLayoutQa = async () => {
    if (isOriginalFormat) {
      toast({ title: 'Layout QA is for the RDR Blue design', description: 'The Original Format copy keeps your own fonts and spacing; line fitting is measured on the RDR Blue version. Switch the template to run it.' });
      return;
    }
    const rawJson = tailoringResults?.Resume_JSON ||
      (isResumeJsonLike(tailoringResults?.Final_Resume_Text) ? tailoringResults.Final_Resume_Text : null) ||
      jsonResumeData;
    const flat = flattenClaudeUnifiedJson(rawJson);
    if (!flat?.CareerHighlights && !flat?.ProfessionalExperience) {
      toast({ title: 'No resume data', description: 'Resume JSON is not available to fit.', variant: 'destructive' });
      return;
    }

    setIsFitting(true);
    try {
      const { findQaIssues, verifyFix, balanceSkillRows } = await import('@/lib/resumeLayoutQa');
      const { applyFits, jobBullets } = await import('@/lib/resumeFit');

      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error('You are not signed in.');
      const FIT_URL = 'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/tailor-resume-claude-jd';
      const FIT_APIKEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I';

      // Width-balance skill rows first (order preserved; only row breaks move).
      // This is pure measurement — no server call — and can require a re-render
      // even when every text line is already within its character range.
      let skillsRebalanced = false;
      for (const [field, prefix] of [['SkillRows', 'Skill'], ['TechnicalSkillRows', 'TechSkill']] as const) {
        const balanced = balanceSkillRows((flat as any)[field], prefix);
        if (balanced.changed) {
          (flat as any)[field] = balanced.rows;
          skillsRebalanced = true;
        }
      }

      // Summary runt detection stays local (canvas wrap). Highlights and bullets
      // are measured on the server with the real Aptos fonts (Marcus, Oct 24
      // 2026: highlights exactly 2 full lines, bullets 1 line), so EVERY one is
      // sent and the server returns only the ones that had to change.
      // The summary rectangle is measured on the server as well (real Aptos,
      // indent search 0-15pt, last line must end 0-5pt short of the edge).
      const summaryText = String((flat as any).Summary ?? '').trim();
      const summaryIssues = summaryText ? [{ id: 's', kind: 'summary' as const, text: summaryText, label: 'Career Summary (rectangle)', min_chars: undefined as number | undefined, max_chars: undefined as number | undefined }] : [];
      void findQaIssues;
      const measuredItems: Array<{ id: string; kind: 'highlight' | 'pe_bullet'; text: string; chtitle?: string; label: string }> = [];
      ((flat as any).CareerHighlights ?? []).forEach((h: any, i: number) => {
        const desc = (h?.chdescription ?? '').trim();
        if (desc) measuredItems.push({ id: `h${i}`, kind: 'highlight', text: desc, chtitle: (h?.chtitle ?? '').trim(), label: `Highlight #${i + 1} (${(h?.chtitle ?? '').trim()})` });
      });
      ((flat as any).ProfessionalExperience ?? []).forEach((j: any, ji: number) => {
        jobBullets(j).forEach((b: any, bi: number) => {
          const t = (b?.Description ?? '').trim();
          if (t) measuredItems.push({ id: `e${ji}.${bi}`, kind: 'pe_bullet', text: t, label: `${j?.company ?? 'Job'} bullet ${bi + 1}` });
        });
      });
      const issues: Array<{ id: string; kind: string; text: string; label: string; chtitle?: string; min_chars?: number; max_chars?: number }> = [
        ...summaryIssues.map((it) => ({ id: it.id, kind: it.kind, text: it.text, label: it.label, min_chars: it.min_chars, max_chars: it.max_chars })),
        ...measuredItems,
      ];
      // No early return when nothing needs fixing: Layout QA is also the only
      // way to pull a FRESH render (new polish spacing, template fixes) for a
      // document that is already in range.
      const nothingToFix = issues.length === 0 && !skillsRebalanced;

      // One call — the server runs the full measure → fix → re-measure loop
      // (up to 4 rounds) with the calibrated width checker, and only returns a
      // line once it PASSES its threshold. Unresolved lines come back separately.
      // A 5xx (Claude API overload blip) gets ONE automatic retry after 3s.
      const fitBody = JSON.stringify({
        stage: 6,
        measure: true,
        ...ledgerRunFields(),
        items: issues.map((it) => ({
          id: it.id,
          text: it.text,
          kind: it.kind,
          chtitle: it.chtitle,
          min_chars: it.min_chars,
          max_chars: it.max_chars,
        })),
      });
      const fitFetch = () => fetch(FIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: FIT_APIKEY },
        body: fitBody,
        signal: AbortSignal.timeout(160_000), // never spin longer than the server can run
      });
      // Skill-rows-only pass needs no server call — the balancing is done.
      let j: any = { success: true, items: [], unresolved: [] };
      if (issues.length > 0) {
        let res = await fitFetch();
        if (!res.ok && res.status >= 500) {
          await new Promise((r) => setTimeout(r, 3000));
          res = await fitFetch();
        }
        if (!res.ok) throw new Error(`Layout QA failed — HTTP ${res.status}: ${(await res.text()).slice(0, 200)}`);
        j = await res.json();
        if (!j?.success || !Array.isArray(j.items)) throw new Error(j?.error || 'Layout QA failed');
      }

      // Apply passed fixes, plus best-effort texts for unresolved ones (they're
      // measurably narrower than the originals even if still over threshold).
      const unresolved: Array<{ id: string; text: string }> = Array.isArray(j.unresolved) ? j.unresolved : [];
      const fixes: Array<{ id: string; text: string }> = [
        ...j.items,
        ...unresolved.filter((u) => u.text && u.text !== issues.find((it) => it.id === u.id)?.text),
      ];
      // (The summary's flushness is verified on the server by measurement now.)
      const totalFixed = (j.items as unknown[]).length;
      const measuredBad: number = Array.isArray(j.measured) ? j.measured.length : 0;
      const remaining = unresolved.map((u) => issues.find((it) => it.id === u.id)?.label ?? u.id);
      let working = applyFits(flat, fixes) as typeof flat;
      if (j?.extras?.SummaryBoldPhrase) (working as any).SummaryBoldPhrase = String(j.extras.SummaryBoldPhrase);
      void verifyFix; // width verification runs server-side now

      // Record exactly what was edited so the user can open it.
      setFitChanges(fixes.flatMap((f) => {
        const src = issues.find((it) => it.id === f.id);
        return src && f.text?.trim() && f.text.trim() !== src.text
          ? [{ label: src.label, before: src.text, after: f.text.trim() }] : [];
      }));
      setFitEngine('Layout QA');

      // Persist into the nested Resume_JSON (same as Perfect Fit).
      if (rawJson?.documents) {
        const nested = structuredClone(rawJson);
        const doc = nested.documents.find((d: any) => d.documentType === 'resume');
        if (doc?.data) {
          doc.data.CareerHighlights = working.CareerHighlights;
          doc.data.ProfessionalExperience = working.ProfessionalExperience;
          if ((working as any).Summary) doc.data.Summary = (working as any).Summary;
          if ((working as any).SummaryBoldPhrase) doc.data.SummaryBoldPhrase = (working as any).SummaryBoldPhrase;
          if ((working as any).SkillRows) doc.data.SkillRows = (working as any).SkillRows;
          if ((working as any).TechnicalSkillRows) doc.data.TechnicalSkillRows = (working as any).TechnicalSkillRows;
        }
        setTailoringResults((prev: any) => ({ ...prev, Resume_JSON: nested }));
      } else {
        setJsonResumeData(working);
        // Keep Resume_JSON in step: template switches rebuild from it.
        setTailoringResults((prev: any) => (prev?.Resume_JSON ? { ...prev, Resume_JSON: working } : prev));
      }

      // SPEED: when the measured pass changed nothing, the document already on
      // screen is the final one: skip the second render and upload.
      const measuredBroken = Array.isArray(j?.measured) ? j.measured.length : 0;
      if (measuredBroken === 0 && fixes.length === 0 && !skillsRebalanced && !(j?.extras?.SummaryBoldPhrase)) {
        toast({ title: 'Already fits', description: 'Every line measured correctly; no changes were needed.' });
        return;
      }
      if (fixes.length === 0 && measuredBroken > 0) {
        toast({ title: "Sorry, we couldn't reformat your resume right now", description: 'Please try again in a few minutes.' });
        return;
      }
      // Re-render with the current template.
      const resumeResponse = await BulkResumeService.generateBulkResumes(working, selectedDocmosisTemplate);
      if (!resumeResponse.success || resumeResponse.files.length === 0) {
        throw new Error('Document re-render failed after Layout QA.');
      }
      const file = resumeResponse.files[0];
      const binaryString = atob(file.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const storagePath = `generated_${Date.now()}_${file.filename.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error: uploadError } = await supabase.storage
        .from('word-documents')
        .upload(storagePath, blob, { cacheControl: '3600', upsert: false });
      let publicUrl: string | undefined;
      if (!uploadError) {
        publicUrl = supabase.storage.from('word-documents').getPublicUrl(storagePath).data?.publicUrl;
      }
      setGeneratedWordDocs(prev => ({ ...prev, resume: { ...file, publicUrl } }));

      toast({
        title: nothingToFix || (totalFixed === 0 && remaining.length === 0) ? 'Already fits — document re-rendered' : 'Layout QA applied',
        description: nothingToFix || (totalFixed === 0 && remaining.length === 0) ? 'Every highlight measures exactly two lines and every bullet one line (real Aptos metrics); the document was re-rendered.' : (remaining.length === 0
          ? `Fixed ${totalFixed} of ${measuredBad} line${measuredBad === 1 ? '' : 's'} that broke the line rules — each verified by measurement.`
          : `Fixed ${totalFixed} line${totalFixed === 1 ? '' : 's'}; ${remaining.length} still off after 4 measured rounds (${remaining.join(', ')}).`)
          + (skillsRebalanced ? ' Skill rows re-broken for even widths.' : '')
          + ' Click "View Edits" for details.',
      });
    } catch (err: any) {
      console.error('Layout QA failed:', err);
      // Users never see a technical error here (Marcus, Oct 25 2026).
      toast({ title: "Sorry, we couldn't reformat your resume right now", description: 'Please try again in a few minutes.' });
    } finally {
      setIsFitting(false);
    }
  };

  // ── Named Groups (Named Groups Playbook, Marcus Oct 24 2026) ─────────────
  // The render report says a job moved whole to the next page and left >= 1in
  // of white space (Case 1), and how many bullets fit under its title. Ask the
  // tailoring function to sort that job's bullets into two honest named groups
  // (never added, dropped or reworded), write the groups into the resume JSON,
  // re-render, and record what changed. One attempt per job per session.
  const namedGroupsTried = useRef<Set<string>>(new Set());
  const [namedGroupsBusy, setNamedGroupsBusy] = useState(false);
  useEffect(() => {
    const report: any = (generatedWordDocs.resume as any)?.polish;
    if (isOriginalFormat) return;
    const sugg: any[] = Array.isArray(report?.suggestions) ? report.suggestions.filter((x: any) => x?.kind === 'namedGroups') : [];
    if (!sugg.length) return;
    if (isFitting || namedGroupsBusy) return;
    if (autoFixFormatting && docmosisGenerationAttempted && !autoFitFinished) return; // let Layout QA finish first
    const todo = sugg.find((x: any) => !namedGroupsTried.current.has(`${savedSessionRowId ?? 'live'}:${x.jobIndex}`));
    if (!todo) return;
    namedGroupsTried.current.add(`${savedSessionRowId ?? 'live'}:${todo.jobIndex}`);
    const run = async () => {
      setNamedGroupsBusy(true);
      try {
        const rawJson = tailoringResults?.Resume_JSON ||
          (isResumeJsonLike(tailoringResults?.Final_Resume_Text) ? tailoringResults.Final_Resume_Text : null) ||
          jsonResumeData;
        const flat: any = flattenClaudeUnifiedJson(rawJson);
        const job = flat?.ProfessionalExperience?.[todo.jobIndex];
        if (!job || Array.isArray(job.Groups) && job.Groups.length) return;
        const bullets: string[] = (job.Descriptions ?? []).map((b: any) => String(b?.Description ?? '').trim()).filter(Boolean);
        if (bullets.length < 4 || todo.group1Bullets < 2 || bullets.length - todo.group1Bullets < 2) return;
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token;
        if (!token) return;
        const res = await fetch('https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/tailor-resume-claude-jd', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I' },
          body: JSON.stringify({ ...ledgerRunFields(), stage: 11, job: { jobTitle: job.job_title, company: job.company, bullets }, group1Count: todo.group1Bullets, targetJobTitle: tailoringResults?.job_title || jobTitle || '' }),
        });
        const j = await res.json().catch(() => null);
        if (!res.ok || !j?.success) throw new Error(j?.error || `HTTP ${res.status}`);
        if (!j.split) {
          setFitChanges((prev) => [...prev, { label: `Named groups NOT applied: ${job.job_title ?? 'job'} (${todo.gapPt}pt gap on page ${todo.page})`, before: bullets.join('\n'), after: `Not split: ${j.reason ?? 'no honest two-theme split'}` }]);
          return;
        }
        // Exact partition check on the client too: same bullets, nothing changed.
        const returned = j.groups.flatMap((g: any) => g.bullets as string[]);
        if (returned.length !== bullets.length || [...returned].sort().join('\u0001') !== [...bullets].sort().join('\u0001')) throw new Error('group split changed the bullets; refused');
        const working = structuredClone(flat);
        const wj = working.ProfessionalExperience[todo.jobIndex];
        wj.Groups = j.groups.map((g: any) => ({ label: g.label, Descriptions: g.bullets.map((t: string) => ({ Description: t })) }));
        wj.Descriptions = [];
        if (rawJson?.documents) {
          const nested = structuredClone(rawJson);
          const doc = nested.documents.find((d: any) => d.documentType === 'resume');
          if (doc?.data) doc.data.ProfessionalExperience = working.ProfessionalExperience;
          setTailoringResults((prev: any) => ({ ...prev, Resume_JSON: nested }));
        } else {
          setJsonResumeData(working);
          // Keep Resume_JSON in step: template switches rebuild from it.
          setTailoringResults((prev: any) => (prev?.Resume_JSON ? { ...prev, Resume_JSON: working } : prev));
        }
        const resumeResponse = await BulkResumeService.generateBulkResumes(working, selectedDocmosisTemplate);
        if (!resumeResponse.success || resumeResponse.files.length === 0) throw new Error('re-render failed after grouping');
        const file = resumeResponse.files[0];
        const binaryString = atob(file.data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        const storagePath = `generated_${Date.now()}_${file.filename.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const { error: uploadError } = await supabase.storage.from('word-documents').upload(storagePath, blob, { cacheControl: '3600', upsert: false });
        const publicUrl = uploadError ? undefined : supabase.storage.from('word-documents').getPublicUrl(storagePath).data?.publicUrl;
        setGeneratedWordDocs((prev) => ({ ...prev, resume: { ...file, publicUrl } }));
        const gapsAfter: number[] = Array.isArray((file as any)?.polish?.pageGapsPt) ? (file as any).polish.pageGapsPt : [];
        const gapAfter = gapsAfter[todo.page - 1];
        setFitChanges((prev) => [...prev, {
          label: `Named groups: ${job.job_title ?? 'job'} at ${job.company ?? ''} — page ${todo.page} gap ${(todo.gapPt / 72).toFixed(2)}in → ${gapAfter !== undefined ? (gapAfter / 72).toFixed(2) + 'in' : 'see new render'}`,
          before: bullets.map((b, i) => `${i + 1}. ${b}`).join('\n'),
          after: j.groups.map((g: any) => `${g.label}\n` + g.bullets.map((b: string) => `  • ${b}`).join('\n')).join('\n\n'),
        }]);
        toast({ title: 'Job split into two named groups', description: `${job.job_title ?? 'Job'}: "${j.groups[0].label}" / "${j.groups[1].label}". Page ${todo.page} gap was ${(todo.gapPt / 72).toFixed(1)}in. Click "View Edits" for the groups.` });
      } catch (err: any) {
        console.error('Named groups failed:', err);
        toast({ title: 'Named groups not applied', description: err?.message || 'Could not split the job.', variant: 'destructive' });
      } finally {
        setNamedGroupsBusy(false);
      }
    };
    void run();
  }, [generatedWordDocs.resume, isFitting, autoFitFinished, autoFixFormatting, docmosisGenerationAttempted, namedGroupsBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-fix formatting ──────────────────────────────────────────────────
  // Runs Layout QA once, unprompted, as soon as a FRESH generation has
  // produced the resume document. Library re-opens never trigger it
  // (docmosisGenerationAttempted stays false there), and the per-run switch
  // on the tailoring page turns it off.
  useEffect(() => {
    if (!autoFixFormatting) return;
    if (isOriginalFormat) return;
    if (autoFitAttempted.current) return;
    // The tailoring run already fitted every line by measurement before the
    // first render: do not fit (and re-render) a second time.
    if ((location.state as any)?.fittedInRun) { autoFitAttempted.current = true; setAutoFitFinished(true); return; }
    if (!docmosisGenerationAttempted) return;
    if (!generatedWordDocs.resume) return;
    if (isFitting) return;
    autoFitAttempted.current = true;
    console.log('🧹 Auto-fix formatting: running Layout QA…');
    Promise.resolve(handleLayoutQa()).finally(() => setAutoFitFinished(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFixFormatting, docmosisGenerationAttempted, generatedWordDocs.resume, isFitting]);

  // ── Letters always match the resume's colour ───────────────────────────────
  // Runs whenever a letter or the template changes, including on a reopened
  // session. Only the banner changes, and only when it differs. No Claude call.
  const recoloredRef = useRef(new Set<string>());
  useEffect(() => {
    if (!selectedDocmosisTemplate.startsWith('rdr-blue-theme')) return;
    for (const key of ['coverLetter', 'thankYouLetter', 'outreachLetter'] as const) {
      const doc: any = (generatedWordDocs as any)[key];
      if (!doc?.publicUrl) continue;
      const mark = `${selectedDocmosisTemplate}|${doc.publicUrl}`;
      if (recoloredRef.current.has(mark)) continue;
      recoloredRef.current.add(mark);
      (async () => {
        const { data: rc, error: rcErr } = await supabase.functions.invoke('generate-bulk-letters', {
          body: { action: 'recolor-saved', docxUrl: doc.publicUrl, templateId: selectedDocmosisTemplate },
        });
        if (rcErr || !rc?.success) { console.error('Letter colour match failed:', key, rcErr ?? rc?.error); return; }
        if (rc.unchanged) return;
        const bin = atob(rc.data);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const path = `generated_${Date.now()}_${String(doc.filename || key).replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const { error: upErr } = await supabase.storage.from('word-documents').upload(path, new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }), { cacheControl: '3600', upsert: false });
        if (upErr) { console.error('Letter colour upload failed:', upErr); return; }
        const publicUrl = supabase.storage.from('word-documents').getPublicUrl(path).data?.publicUrl;
        if (!publicUrl) return;
        recoloredRef.current.add(`${selectedDocmosisTemplate}|${publicUrl}`);
        setGeneratedWordDocs((prev: any) => ({ ...prev, [key]: { ...prev[key], data: rc.data, publicUrl } }));
      })();
    }
  }, [generatedWordDocs, selectedDocmosisTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist the generated documents ───────────────────────────────────────
  // Every time the resume (or a letter) is (re)rendered and uploaded, write its
  // public URL + the JSON it was rendered from + the template onto the session
  // row, so a refresh or a library re-open shows the same file without calling
  // the renderer or the analysis again. Merge, never overwrite, analysis_results.
  const lastPersistedDocsRef = useRef<string>('');
  useEffect(() => {
    const rowId = savedSessionRowId;
    if (!rowId) return;
    const resume = generatedWordDocs.resume;
    if (!resume?.publicUrl) return;
    const snapshot: Record<string, unknown> = { template: selectedDocmosisTemplate };
    for (const k of ['resume', 'coverLetter', 'thankYouLetter', 'outreachLetter', 'linkedin'] as const) {
      const d = (generatedWordDocs as any)[k];
      if (d?.publicUrl) snapshot[k] = { filename: d.filename, size: d.size, publicUrl: d.publicUrl, polish: (d as any).polish ?? null, savedAt: new Date().toISOString() };
    }
    const key = JSON.stringify([rowId, resume.publicUrl, selectedDocmosisTemplate, Object.keys(snapshot).sort(), ...['coverLetter', 'thankYouLetter', 'outreachLetter'].map((k) => (generatedWordDocs as any)[k]?.publicUrl ?? '')]);
    if (lastPersistedDocsRef.current === key) return;
    lastPersistedDocsRef.current = key;
    (async () => {
      try {
        const { data: row } = await supabase.from('tailored_documents').select('analysis_results').eq('id', rowId).single();
        const merged = { ...((row?.analysis_results as Record<string, unknown>) ?? {}), _docs: snapshot };
        const currentJson = tailoringResults?.Resume_JSON || jsonResumeData || null;
        const { error: upErr } = await supabase.from('tailored_documents').update({
          analysis_results: merged,
          word_document_url: resume.publicUrl,
          ...(currentJson ? { document_json_data: currentJson } : {}),
          updated_at: new Date().toISOString(),
        }).eq('id', rowId);
        if (upErr) throw upErr;
        console.log('Generated documents persisted to session row', rowId, Object.keys(snapshot), 'json', !!currentJson);
        // Mark the session FINAL only when the fitting has finished (or was off)
        // and the saved render has no line-rule blockers: then a refresh shows
        // this saved file instead of re-running the fitting.
        try {
          const sid = new URLSearchParams(window.location.search).get('sessionId');
          const pol: any = (resume as any).polish;
          const clean = !pol || pol.ok !== false;
          const fittingDone = !autoFixFormatting || autoFitFinished || !docmosisGenerationAttempted;
          if (sid && clean && fittingDone && !isFitting && !namedGroupsBusy) sessionStorage.setItem(`tw_final_${sid}`, '1');
        } catch { /* storage unavailable: refresh simply re-runs, as before */ }
      } catch (e) {
        // Let the next change (or the next row id) retry instead of silently giving up.
        lastPersistedDocsRef.current = '';
        console.error('Could not persist generated documents:', e);
      }
    })();
  }, [savedSessionRowId, generatedWordDocs, selectedDocmosisTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark the session final once the fitting has finished and the saved file
  // is clean (the persist effect may have run before autoFitFinished flipped).
  useEffect(() => {
    const resume: any = generatedWordDocs.resume;
    if (!resume?.publicUrl || !savedSessionRowId || isFitting || namedGroupsBusy) return;
    if (autoFixFormatting && docmosisGenerationAttempted && !autoFitFinished) return;
    if (resume.polish && resume.polish.ok === false) return;
    try {
      const sid = new URLSearchParams(window.location.search).get('sessionId');
      if (sid) sessionStorage.setItem(`tw_final_${sid}`, '1');
    } catch { /* ignore */ }
  }, [generatedWordDocs.resume, savedSessionRowId, isFitting, namedGroupsBusy, autoFitFinished, autoFixFormatting, docmosisGenerationAttempted]);

  // ── When the match analysis may run ──────────────────────────────────────
  // Only once the resume DOCUMENT exists, and (when auto-fix is on) only after
  // the auto Layout QA pass has re-rendered it. The analysis then scores the
  // Resume_JSON that produced that final document — the same object the
  // Word file was rendered from — not the pre-fit draft the tailoring page
  // handed over. A saved analysis (library re-open) displays as saved.
  const finalDocumentReady = !!generatedWordDocs.resume && !isFitting && !namedGroupsBusy && (!autoFixFormatting || autoFitFinished || !docmosisGenerationAttempted);
  const baseAnalysisInputs = (location.state as any)?.claudeDirectAnalysis ?? dbResultsState?.claudeDirectAnalysis ?? null;
  const analysisInputs = useMemo(() => {
    if (!baseAnalysisInputs) return null;
    const finalJson = tailoringResults?.Resume_JSON ?? baseAnalysisInputs.resume_json;
    return { ...baseAnalysisInputs, resume_json: finalJson };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseAnalysisInputs, finalDocumentReady]);


  // Auto-save all tailored documents to library when component mounts - ONCE ONLY
  useEffect(() => {
    console.log('=== SAVE CONDITIONS DEBUG ===');
    console.log('user?.id:', user?.id);
    console.log('!!tailoringResults:', !!tailoringResults);
    console.log('isSaved:', isSaved);
    console.log('saveAttempted:', saveAttempted);
    console.log('savePromiseRef.current:', savePromiseRef.current);
    console.log('=== USEEFFECT TRIGGERED ===');
    console.log('saveAttempted:', saveAttempted);
    console.log('!!tailoringResults:', !!tailoringResults); 
    console.log('!!user?.id:', !!user?.id);
    console.log('savePromiseRef.current:', savePromiseRef.current);
    
    if (saveAttempted || !tailoringResults || !user?.id || savePromiseRef.current) {
      console.log('=== SAVE BLOCKED ===');
      console.log('Reason - saveAttempted:', saveAttempted);
      console.log('Reason - no tailoringResults:', !tailoringResults);
      console.log('Reason - no user.id:', !user?.id);
      console.log('Reason - promise exists:', !!savePromiseRef.current);
      return;
    }
    
    // A finished run is saved and charged ONCE. Reloading this page (refresh,
    // back/forward) keeps the same router state; without this guard every
    // reload saved a duplicate copy and charged the credits again.
    const runId: string | undefined = (location.state as any)?.runId;
    const runKey = runId ? `tw_run_saved_${runId}` : null;
    try {
      const prior = runKey ? localStorage.getItem(runKey) : null;
      // 'saved' = done. A 'saving:<timestamp>' older than 10 minutes is a save
      // that never finished (tab closed mid-save): let it try again rather than
      // silently never saving this run.
      const staleSaving = prior && prior.startsWith('saving:') && Date.now() - Number(prior.slice(7)) > 10 * 60_000;
      if (runKey && prior && !staleSaving) {
        console.log('=== SAVE SKIPPED: this run was already saved and charged ===', runId, prior);
        setSaveAttempted(true);
        setIsSaved(true);
        return;
      }
      if (runKey) localStorage.setItem(runKey, `saving:${Date.now()}`);
    } catch { /* storage blocked: fall through to the normal single save */ }

    console.log('=== PROCEEDING WITH SAVE ===');
    setSaveAttempted(true);
    
    const autoSaveTailoredDocuments = async () => {

      try {
        console.log('=== SAVING TAILORED DOCUMENTS ===');
        console.log('selectedDocuments:', selectedDocuments);
        console.log('tailoringResults keys:', Object.keys(tailoringResults));
        console.log('user.id:', user?.id);
        console.log('jobTitle:', jobTitle);
        console.log('companyName:', companyName);
        console.log('Available content:');
        console.log('- Final_Resume_Text:', !!tailoringResults.Final_Resume_Text);
        console.log('- Cover_Letter:', !!tailoringResults.Cover_Letter);
        console.log('- Cover_Letter type:', typeof tailoringResults.Cover_Letter);
        // Use safeString to handle both string and object Cover_Letter
        console.log('- Cover_Letter content preview:', safePreview(tailoringResults.Cover_Letter, 100) || 'NULL/UNDEFINED');
        console.log('- Thank_You_Letter:', !!tailoringResults.Thank_You_Letter);
        console.log('- Executive_Bio:', !!tailoringResults.Executive_Bio);
        console.log('- LinkedIn_Profile (capital I):', !!tailoringResults.LinkedIn_Profile);
        console.log('- Linkedin_Profile (lowercase i):', !!tailoringResults.Linkedin_Profile);
        console.log('- Outreach_Letter:', !!tailoringResults.Outreach_Letter);
        
        // Log selectedDocuments to see what was requested
        console.log('Selected documents that should be available:', selectedDocuments);
        
        // Check if Cover_Letter was selected but not received
        if (selectedDocuments.includes('cover-letter') || selectedDocuments.includes('coverLetter')) {
          console.log('🚨 Cover letter was SELECTED but content is:', tailoringResults.Cover_Letter || 'MISSING');
        }
        
        // Fix selectedDocuments format - ensure it always includes 'resume' and normalize naming
        let normalizedSelectedDocuments = ['resume']; // Always include resume
        
        if (selectedDocuments.includes('cover-letter') || selectedDocuments.includes('coverLetter')) {
          normalizedSelectedDocuments.push('cover-letter');
        }
        if (selectedDocuments.includes('thank-you-letter') || selectedDocuments.includes('thankYouLetter')) {
          normalizedSelectedDocuments.push('thank-you-letter');
        }
        if (selectedDocuments.includes('executive-bio') || selectedDocuments.includes('executiveBio')) {
          normalizedSelectedDocuments.push('executive-bio');
        }
        if (selectedDocuments.includes('linkedin-profile') || selectedDocuments.includes('linkedinProfile')) {
          normalizedSelectedDocuments.push('linkedin-profile');
        }
        if (selectedDocuments.includes('outreach-letter') || selectedDocuments.includes('outreach-email') || selectedDocuments.includes('outreachLetter') || selectedDocuments.includes('outreachEmail')) {
          // Canonical id is 'outreach-letter'. Old saved sessions used 'outreach-email'.
          normalizedSelectedDocuments.push('outreach-letter');
        }
        
        console.log('Normalized selectedDocuments:', normalizedSelectedDocuments);
        
        // Check if we have any content to save
        const hasContent = tailoringResults.Final_Resume_Text || 
                          tailoringResults.Cover_Letter || 
                          tailoringResults.Thank_You_Letter ||
                          tailoringResults.Executive_Bio ||
                          tailoringResults.LinkedIn_Profile ||
                          tailoringResults.Linkedin_Profile ||
                          tailoringResults.Outreach_Letter;
        
        console.log('hasContent:', hasContent);
        if (!hasContent) {
          console.error('No content found to save!');
          throw new Error('No content found in tailoring results');
        }

        // Calculate detailed score metrics and add to tailoring results
        const scoreJump = afterScore && beforeScore ? afterScore - beforeScore : 0;
        const percentageImprovement = beforeScore && afterScore && beforeScore > 0 
          ? ((afterScore - beforeScore) / beforeScore * 100).toFixed(1)
          : '0';
        
        const enhancedTailoringResults = {
          ...tailoringResults,
          Original_Match_Score: beforeScore,
          Tailored_Match_Score: afterScore,
          original_analysis: originalAnalysis,
          tailored_analysis: tailoredAnalysis,
          improvement_report: improvement,
          detailed_scores: {
            before_score: beforeScore,
            after_score: afterScore,
            score_jump: scoreJump,
            percentage_improvement: percentageImprovement,
            tailoring_timestamp: new Date().toISOString()
          }
        };

        console.log('About to call TailoredDocumentService.saveTailoringSession with:', {
          enhancedTailoringResults: Object.keys(enhancedTailoringResults),
          jobTitle: jobTitle || '',
          companyName: companyName || '',
          selectedDocuments
        });

        // Save the tailoring session to the database - use normalizedSelectedDocuments!
        const sessionId = await TailoredDocumentService.saveTailoringSession(
          enhancedTailoringResults,
          (tailoringResults?.job_title || '').trim(),
          (tailoringResults?.company_name || '').trim(),
          jobDescription || '',
          normalizedSelectedDocuments, // Use normalized list, not original
          undefined, // originalResumeId
          generatedFilename, // Pass the generated filename
          tailoringDurationFromState || undefined, // Pass the tailoring duration in seconds
          // Everything the results page needs to fully reopen from the library.
          {
            tailoringResults: enhancedTailoringResults,
            jobDescription: jobDescription || '',
            originalResume: originalResumeText || '',
            claudeDirectAnalysis: (location.state as any)?.claudeDirectAnalysis ?? null,
            jobTitle: (location.state as any)?.jobTitle ?? null,
            companyName: (location.state as any)?.companyName ?? null,
            jobUrl: (location.state as any)?.jobUrl ?? null,
            buildCriteria: (location.state as any)?.buildCriteria ?? null,
            tailoringReportPainPoints: (location.state as any)?.tailoringReportPainPoints ?? '',
            selectedDocuments: normalizedSelectedDocuments,
            originalDocxUrl: originalDocxUrl ?? null,
          }
        );
        console.log('Saved tailoring session with ID:', sessionId);

        // Remember the resume row so the stage-4 analysis (which may still be
        // running) can be persisted onto it when it lands.
        try {
          const { data: savedRow } = await supabase
            .from('tailored_documents')
            .select('id')
            .eq('tailoring_session_id', sessionId)
            .eq('document_type', 'resume')
            .limit(1)
            .maybeSingle();
          if (savedRow?.id) {
            setSavedSessionRowId(savedRow.id);
            savedSessionRowIdRef.current = savedRow.id;
            void persistStage4(null); // flush any analysis that finished early
          }
          // The page now has a durable address: a refresh (which drops router
          // state) reloads this exact session from the database instead of
          // showing an empty page or re-running anything.
          try {
            if (!searchParams.get('sessionId')) {
              navigate(`/resume-results-templated?sessionId=${sessionId}`, { replace: true, state: location.state });
            }
          } catch { /* URL update is a convenience; never fail the save */ }
        } catch (rowErr) {
          console.warn('Could not resolve saved session row id:', rowErr);
        }

        console.log('Successfully saved tailoring session:', sessionId);
        
        // NOW deduct credits after successful save
        console.log('=== DEDUCTING CREDITS AFTER SUCCESSFUL SAVE ===');
        try {
          const deductResult = await CreditService.deductCredits('resume_tailoring', {
            sessionId,
            jobTitle: tailoringResults?.job_title || jobTitle || '',
            companyName: tailoringResults?.company_name || companyName || '',
            documentsSelected: normalizedSelectedDocuments
          });
          
          if (deductResult.success) {
            console.log('Credits deducted successfully:', deductResult.credits_used);
            toast({
              title: "Documents saved & credits applied!",
              description: `${deductResult.credits_used} credits used. ${deductResult.new_balance} remaining.`,
            });
          } else {
            console.error('Credit deduction failed:', deductResult.error);
            // Still show success for save, but warn about credits
            toast({
              title: "Documents saved to library!",
              description: `Note: Credit deduction failed - ${deductResult.error}`,
              variant: "default"
            });
          }
        } catch (creditError: any) {
          console.error('Credit deduction error:', creditError);
          toast({
            title: "Documents saved to library!",
            description: "Your documents were saved but credit deduction encountered an issue.",
          });
        }
        
        setIsSaved(true);
        try { if (runKey) localStorage.setItem(runKey, 'saved'); } catch { /* noop */ }
      } catch (error) {
        // Save failed: nothing was charged, so allow a later retry for this run.
        try { if (runKey) localStorage.removeItem(runKey); } catch { /* noop */ }
        console.error('DETAILED SAVE ERROR:', error);
        console.error('Error stack:', error.stack);
        console.error('Error details:', {
          message: error.message,
          name: error.name,
          tailoringResults: !!tailoringResults,
          userId: !!user?.id,
          selectedDocuments
        });
        toast({
          title: "Auto-save failed",
          description: `Error: ${error.message || 'Unknown error occurred'}`,
          variant: "destructive"
        });
      }
    };

    savePromiseRef.current = autoSaveTailoredDocuments();
  }, []); // Empty dependency array - run only once on mount

  // Format resume content with proper styling - GENERIC FORMAT
  const formatResumeContent = (content: string) => {
    return content
      // Format name (first line) - make it bold, blue, and centered
      .replace(/^(.+?)\n/, '<div class="text-center"><h1 class="text-xl font-bold text-blue-600 mb-1">$1</h1></div>')
      // Format contact info (lines with email, phone, etc.) - center them with separator line
      .replace(/^(.+[@•].+)$/gm, '<div class="text-center text-sm text-gray-600 mb-3">$1</div><hr class="border-gray-300 my-3">')
      // Format section headings (lines in ALL CAPS or title case followed by content)
      .replace(/^([A-Z][A-Z\s&]+)$/gm, '<h2 class="text-lg font-bold text-blue-600 mt-1 mb-1 border-b border-blue-200 pb-1">$1</h2>')
      // Format career highlights bullets with checkmarks and bold headings
      .replace(/^•\s*([^:]+):\s*(.+)$/gm, '<div class="ml-4 mb-1"><span class="text-green-600">✓</span> <strong>$1:</strong> $2</div>')
      // Format regular bullet points
      .replace(/^•\s*(.+)$/gm, '<div class="ml-4">• $1</div>')
      // Remove multiple spaces and excessive line breaks
      .replace(/\s{3,}/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\n\n/g, '<br>')
      .replace(/\n/g, '');
  };

  // Format cover letter and other documents with proper spacing
  const formatLetterContent = (content: string) => {
    return content
      // Replace placeholder company name and job title with actual values  
      .replace(/\[?Company Name\]?/g, companyName)
      .replace(/\[?Job Title\]?/g, jobTitle)
      .replace(/\[?Position\]?/g, jobTitle)
      // Format the header with name, contact, date, and company
      .replace(/^(.+?)\n(.+[@•].+)\n\n(\[?Date\]?.*)\n(.+)/m, 
        '<div class="text-center"><div class="text-lg font-bold">$1</div><div class="text-sm text-gray-600 border-b border-gray-300 pb-2 mb-4">$2</div></div><div class="mb-2">$3</div><div class="mb-2">$4</div>')
      // Add proper spacing between paragraphs
      .replace(/\n\n/g, '<br><br>')
      // Convert remaining line breaks
      .replace(/\n/g, '<br>');
  };

  // Handle opening Word viewer - opens ALL documents as tabs
  const handleOpenInWordViewer = async () => {
    if (documents.length === 0) {
      toast({
        title: "No documents available",
        description: "There are no documents to open in Word viewer.",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Preparing Documents",
      description: `Opening ${documents.length} document(s) in Word Viewer...`,
    });

    try {
      // Build array of ALL documents with their JSON data
      const multipleDocuments: any[] = [];

      for (const doc of documents) {
        console.log(`[handleOpenInWordViewer] Processing document: ${doc.type}, id: ${doc.id}`);
        
        if (doc.type === 'resume') {
          // For resume, get or convert JSON
          let resumeDataToUse = jsonResumeData;
          if (!resumeDataToUse) {
            resumeDataToUse = await convertResumeToJson();
          }
          if (resumeDataToUse) {
            const personNameForFile = resumeDataToUse.Name || personName || '';
            const resumeJobTitle = resumeDataToUse.ProfessionalExperience?.[0]?.job_title || '';
            const currentDate = new Date().toLocaleString('en-US', { month: 'short', year: 'numeric' });
            const filenameCore = [personNameForFile, resumeJobTitle].filter(Boolean).join(' - ');
            const filename = `${filenameCore || (tailoringResults?.run_id ? `Run-${tailoringResults.run_id.slice(0, 8)}` : 'Run')}` + ` - ${currentDate}.docx`;
            multipleDocuments.push({
              resumeJson: resumeDataToUse,
              documentType: 'resume',
              filename: filename,
              // Open the SAVED fitted file as-is; re-rendering from the JSON
              // showed an older version (Oct 25 2026).
              existingFileUrl: generatedWordDocs.resume?.publicUrl || undefined,
            });
          }
        } else if (doc.type === 'cover-letter') {
          // Require real structured letter JSON; do not fall back to placeholder-filled plain text
          if (!coverLetterJSON?.Body) {
            toast({
              title: 'Cover letter unavailable',
              description: 'VectorShift did not return structured Cover_Letter JSON (missing Body).',
              variant: 'destructive'
            });
            continue;
          }

          // Get contact info from Resume_JSON for all letters (no fake fallback strings)
          const contactInfo = {
            Name:
              tailoringResults?.Resume_JSON?.Name ||
              jsonResumeData?.Name ||
              (typeof tailoringResults?.Final_Resume_Text === 'object' ? tailoringResults.Final_Resume_Text?.Name : '') ||
              personName ||
              '',
            Phone: tailoringResults?.Resume_JSON?.Phone || jsonResumeData?.Phone || '',
            Email: tailoringResults?.Resume_JSON?.Email || jsonResumeData?.Email || '',
            City: tailoringResults?.Resume_JSON?.City || jsonResumeData?.City || '',
            State: tailoringResults?.Resume_JSON?.State || jsonResumeData?.State || '',
            LinkedIn: tailoringResults?.Resume_JSON?.LinkedIn || jsonResumeData?.LinkedIn || ''
          };

          if (!contactInfo.Name) {
            toast({
              title: 'Missing name for export',
              description: 'No name found in Resume_JSON; cannot generate a proper cover letter document.',
              variant: 'destructive'
            });
            continue;
          }

          const letterJson = {
            Body: coverLetterJSON.Body,
            ...contactInfo,
            CompanyName: companyName || coverLetterJSON?.CompanyName || '',
            JobTitle: jobTitle || coverLetterJSON?.JobTitle || ''
          };

          const coverFilenameParts = [
            contactInfo.Name,
            companyName || '',
            jobTitle || '',
            'Cover Letter',
            currentDate
          ].filter(Boolean);

          multipleDocuments.push({
            resumeJson: letterJson,
            documentType: 'cover_letter',
            filename: doc.file_name || `${coverFilenameParts.join(' - ')}.docx`
          });
        } else if (doc.type === 'thank-you-letter') {
          if (!thankYouJSON?.Body) {
            toast({
              title: 'Thank you letter unavailable',
              description: 'VectorShift did not return structured Thank_You_Letter JSON (missing Body).',
              variant: 'destructive'
            });
            continue;
          }

          const contactInfo = {
            Name: tailoringResults?.Resume_JSON?.Name || jsonResumeData?.Name || personName || '',
            Phone: tailoringResults?.Resume_JSON?.Phone || jsonResumeData?.Phone || '',
            Email: tailoringResults?.Resume_JSON?.Email || jsonResumeData?.Email || ''
          };

          if (!contactInfo.Name) {
            toast({
              title: 'Missing name for export',
              description: 'No name found in Resume_JSON; cannot generate a proper thank you letter document.',
              variant: 'destructive'
            });
            continue;
          }

          const letterJson = {
            Body: thankYouJSON.Body,
            ...contactInfo
          };

          const filenameParts = [contactInfo.Name, companyName || '', jobTitle || '', 'Thank You Letter', currentDate].filter(Boolean);

          multipleDocuments.push({
            resumeJson: letterJson,
            documentType: 'thank_you_letter',
            filename: doc.file_name || `${filenameParts.join(' - ')}.docx`
          });
        } else if (doc.type === 'outreach-letter') {
          if (!outreachJSON?.Body) {
            toast({
              title: 'Outreach letter unavailable',
              description: 'VectorShift did not return structured Outreach_Letter JSON (missing Body).',
              variant: 'destructive'
            });
            continue;
          }

          const contactInfo = {
            Name: tailoringResults?.Resume_JSON?.Name || jsonResumeData?.Name || personName || '',
            Phone: tailoringResults?.Resume_JSON?.Phone || jsonResumeData?.Phone || '',
            Email: tailoringResults?.Resume_JSON?.Email || jsonResumeData?.Email || ''
          };

          if (!contactInfo.Name) {
            toast({
              title: 'Missing name for export',
              description: 'No name found in Resume_JSON; cannot generate a proper outreach letter document.',
              variant: 'destructive'
            });
            continue;
          }

          const letterJson = {
            Body: outreachJSON.Body,
            ...contactInfo
          };

          const filenameParts = [contactInfo.Name, companyName || '', jobTitle || '', 'Outreach Letter', currentDate].filter(Boolean);

          multipleDocuments.push({
            resumeJson: letterJson,
            documentType: 'outreach_letter',
            filename: doc.file_name || `${filenameParts.join(' - ')}.docx`
          });
        } else if (doc.type === 'executive-bio') {
          const contactInfo = {
            Name: tailoringResults?.Resume_JSON?.Name || jsonResumeData?.Name || personName || ''
          };

          if (!contactInfo.Name) {
            toast({
              title: 'Missing name for export',
              description: 'No name found in Resume_JSON; cannot generate a proper executive bio document.',
              variant: 'destructive'
            });
            continue;
          }

          multipleDocuments.push({
            resumeJson: {
              Body: doc.content,
              ...contactInfo
            },
            documentType: 'executive_bio',
            filename: doc.file_name || `${[contactInfo.Name, 'Executive Bio', currentDate].filter(Boolean).join(' - ')}.docx`
          });
        } else if (doc.type === 'linkedin-profile') {
          const contactInfo = {
            Name: tailoringResults?.Resume_JSON?.Name || jsonResumeData?.Name || personName || ''
          };

          if (!contactInfo.Name) {
            toast({
              title: 'Missing name for export',
              description: 'No name found in Resume_JSON; cannot generate a proper LinkedIn profile document.',
              variant: 'destructive'
            });
            continue;
          }

          multipleDocuments.push({
            resumeJson: {
              content: doc.content,
              ...contactInfo
            },
            documentType: 'linkedin_profile',
            filename: doc.file_name || `${[contactInfo.Name, 'LinkedIn Profile', currentDate].filter(Boolean).join(' - ')}.docx`
          });
        }
      }

      // Every saved letter opens in the editor from its saved file, even when
      // the letter text is not on the page (a reopened session).
      const LETTER_TABS: Array<[string, string, string]> = [
        ['coverLetter', 'cover_letter', 'Cover Letter'],
        ['thankYouLetter', 'thank_you_letter', 'Thank You Letter'],
        ['outreachLetter', 'outreach_letter', 'Outreach Letter'],
      ];
      for (const [key, type, label] of LETTER_TABS) {
        const saved: any = (generatedWordDocs as any)[key];
        if (!saved?.publicUrl) continue;
        const existing = multipleDocuments.find((d) => d.documentType === type);
        if (existing) existing.existingFileUrl = saved.publicUrl;
        else multipleDocuments.push({ resumeJson: { Body: '' }, documentType: type, filename: saved.filename || `${personName || 'Letter'} - ${label}.docx`, existingFileUrl: saved.publicUrl });
      }

      console.log(`[handleOpenInWordViewer] Prepared ${multipleDocuments.length} documents for Word Viewer`);

      if (multipleDocuments.length === 0) {
        toast({
          title: "No documents prepared",
          description: "Could not prepare any documents for Word viewer.",
          variant: "destructive"
        });
        return;
      }

      // Navigate with ALL documents AND the currently selected Docmosis
      // template so the editor opens in the same template the user just
      // chose on the results page (instead of always falling back to
      // template2). The viewer reads `state.docmosisTemplateId`.
      navigate('/word-document-viewer', {
        state: {
          multipleDocuments: multipleDocuments,
          docmosisTemplateId: selectedDocmosisTemplate,
          // The AI Assistant uses the target job for keyword edits.
          jobDescription: jobDescription || undefined,
          // The client's own Word file: enables "Original Format" in the editor.
          originalDocxUrl: originalDocxUrl || undefined,
          // The saved session: editor changes are written back to it.
          tailoredRowId: savedSessionRowId || undefined,
          pageCount: Array.isArray((generatedWordDocs.resume as any)?.polish?.pageGapsPt) ? (generatedWordDocs.resume as any).polish.pageGapsPt.length + 1 : undefined,
        },
      });

      toast({
        title: "Opening Word Viewer",
        description: `Generating ${multipleDocuments.length} Word document(s)...`,
      });

    } catch (error) {
      console.error('Error opening Word viewer:', error);
      toast({
        title: "Error",
        description: "Failed to open Word viewer. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle manual save to library (function removed since auto-save is active)

  // Handle PDF export - generates PDF natively via Docmosis (same template, PDF output format)
  const handleExportPDF = async () => {
    setIsExportingPDF(true);
    
    try {
      const pdfFilename = generateCleanFilename(activeDocType, 'pdf');
      let pdfBlob: Blob | null = null;
      const savedUrl = (generatedWordDocs as any)[activeDocType]?.publicUrl as string | undefined;

      if (savedUrl) {
        // Convert the SAVED Word file (the fitted document on screen), so the
        // PDF has exactly the same content and layout fixes as the Word file.
        const { data: pdfRes, error: pdfErr } = await supabase.functions.invoke('generate-bulk-resumes', {
          body: { action: 'saved-docx-to-pdf', docxUrl: savedUrl, fileName: pdfFilename },
        });
        if (pdfErr) throw pdfErr;
        if (!pdfRes?.success || !pdfRes.files?.[0]?.data) throw new Error(pdfRes?.error || 'PDF conversion failed');
        const binaryString = atob(pdfRes.files[0].data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        pdfBlob = new Blob([bytes], { type: 'application/pdf' });
      } else if (activeDocType === 'resume') {
        // Generate resume PDF via Docmosis
        const resumeJsonData = tailoringResults?.Resume_JSON || 
          (isResumeJsonLike(tailoringResults?.Final_Resume_Text) ? tailoringResults.Final_Resume_Text : null) ||
          jsonResumeData;
        
        if (!resumeJsonData) {
          throw new Error('No resume data available for PDF generation');
        }

        const response = await BulkResumeService.generateBulkResumes(
          { ...resumeJsonData, outputFormat: 'pdf' }, 
          selectedDocmosisTemplate
        );
        if (response.success && response.files.length > 0) {
          const file = response.files[0];
          const binaryString = atob(file.data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        }
      } else {
        // Generate letter PDF via Docmosis
        const resumeJson = tailoringResults?.Resume_JSON || 
          (isResumeJsonLike(tailoringResults?.Final_Resume_Text) ? tailoringResults.Final_Resume_Text : null) ||
          jsonResumeData;
        
        const docTypeToLetterKey: Record<string, { jsonKey: string; bodyKey: string; letterTypeKey: string }> = {
          coverLetter: { jsonKey: 'Cover_Letter_JSON', bodyKey: 'coverLetterBody', letterTypeKey: 'coverLetter' },
          thankYouLetter: { jsonKey: 'Thank_You_Letter_JSON', bodyKey: 'thankYouLetterBody', letterTypeKey: 'thankYouLetter' },
          outreachLetter: { jsonKey: 'Outreach_Letter_JSON', bodyKey: 'outreachLetterBody', letterTypeKey: 'outreachLetter' },
        };
        
        const mapping = docTypeToLetterKey[activeDocType];
        if (!mapping) throw new Error(`Unsupported document type for PDF: ${activeDocType}`);
        
        const letterJSON = tailoringResults?.[mapping.jsonKey] || 
          (isVectorShiftLetterJSON(tailoringResults?.[mapping.jsonKey.replace('_JSON', '')]) ? tailoringResults[mapping.jsonKey.replace('_JSON', '')] : null);
        
        if (!letterJSON?.Body) throw new Error(`No ${activeDocType} content available for PDF generation`);
        
        const contactName = resumeJson?.Name || personName || '';
        const [city, state] = (resumeJson?.City && resumeJson?.State) 
          ? [resumeJson.City, resumeJson.State]
          : (resumeJson?.CityST || '').split(',').map((s: string) => s.trim());

        const letterRequest = {
          letters: [{
            headerInfo: {
              fullName: contactName,
              phoneNumber: resumeJson?.Phone || '',
              email: resumeJson?.Email || '',
              cityST: `${city || ''}, ${state || ''}`,
              linkedin: resumeJson?.LinkedIn || '',
              companyName: companyName || letterJSON?.CompanyName || '',
              recipientName: letterJSON?.RecipientName || '',
              jobTitle: jobTitle || letterJSON?.JobTitle || ''
            },
            [mapping.bodyKey]: letterJSON.Body
          }],
          letterTypes: { 
            coverLetter: mapping.letterTypeKey === 'coverLetter', 
            thankYouLetter: mapping.letterTypeKey === 'thankYouLetter', 
            outreachLetter: mapping.letterTypeKey === 'outreachLetter' 
          },
          templateId: selectedDocmosisTemplate,
          outputFormat: 'pdf'
        };

        const letterResponse = await BulkLetterService.generateBulkLetters(letterRequest);
        if (letterResponse.success && letterResponse.files.length > 0) {
          const file = letterResponse.files[0];
          const binaryString = atob(file.data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        }
      }

      if (!pdfBlob) throw new Error('Failed to generate PDF');

      // Download the PDF
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "PDF Downloaded",
        description: `Downloaded ${pdfFilename}`
      });
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        title: "Sorry, we couldn't create your PDF right now",
        description: "Please try again in a few minutes.",
        variant: "destructive"
      });
    } finally {
      setIsExportingPDF(false);
    }
  };

  // Helper: Generate a PDF blob for a given doc type via Docmosis native rendering
  const generateDocmosisPdfBlob = async (docType: string): Promise<Blob | null> => {
    const savedUrl = (generatedWordDocs as any)[docType]?.publicUrl as string | undefined;
    if (savedUrl) {
      const { data: pdfRes, error: pdfErr } = await supabase.functions.invoke('generate-bulk-resumes', {
        body: { action: 'saved-docx-to-pdf', docxUrl: savedUrl, fileName: generateCleanFilename(docType, 'pdf') },
      });
      if (pdfErr || !pdfRes?.success || !pdfRes.files?.[0]?.data) return null;
      const bin = atob(pdfRes.files[0].data);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new Blob([bytes], { type: 'application/pdf' });
    }
    const rawResumeJsonPdf = tailoringResults?.Resume_JSON || 
      (isResumeJsonLike(tailoringResults?.Final_Resume_Text) ? tailoringResults.Final_Resume_Text : null) ||
      jsonResumeData;
    const resumeJson = flattenClaudeUnifiedJson(rawResumeJsonPdf);

    if (docType === 'resume') {
      if (!resumeJson) return null;
      const response = await BulkResumeService.generateBulkResumes(
        { ...resumeJson, outputFormat: 'pdf' }, 
        selectedDocmosisTemplate
      );
      if (response.success && response.files.length > 0) {
        const file = response.files[0];
        const binaryString = atob(file.data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return new Blob([bytes], { type: 'application/pdf' });
      }
      return null;
    }

    // Letters
    const docTypeToLetterKey: Record<string, { jsonKey: string; altKey: string; bodyKey: string; letterTypeKey: string }> = {
      coverLetter: { jsonKey: 'Cover_Letter_JSON', altKey: 'Cover_Letter', bodyKey: 'coverLetterBody', letterTypeKey: 'coverLetter' },
      thankYouLetter: { jsonKey: 'Thank_You_Letter_JSON', altKey: 'Thank_You_Letter', bodyKey: 'thankYouLetterBody', letterTypeKey: 'thankYouLetter' },
      outreachLetter: { jsonKey: 'Outreach_Letter_JSON', altKey: 'Outreach_Letter', bodyKey: 'outreachLetterBody', letterTypeKey: 'outreachLetter' },
    };

    const mapping = docTypeToLetterKey[docType];
    if (!mapping) return null;

    const letterJSON = tailoringResults?.[mapping.jsonKey] || 
      (isVectorShiftLetterJSON(tailoringResults?.[mapping.altKey]) ? tailoringResults[mapping.altKey] : null);
    if (!letterJSON?.Body) return null;

    const contactName = resumeJson?.Name || personName || '';
    const [city, state] = (resumeJson?.City && resumeJson?.State) 
      ? [resumeJson.City, resumeJson.State]
      : (resumeJson?.CityST || '').split(',').map((s: string) => s.trim());

    const letterRequest = {
      letters: [{
        headerInfo: {
          fullName: contactName,
          phoneNumber: resumeJson?.Phone || '',
          email: resumeJson?.Email || '',
          cityST: `${city || ''}, ${state || ''}`,
          linkedin: resumeJson?.LinkedIn || '',
          companyName: companyName || letterJSON?.CompanyName || '',
          recipientName: letterJSON?.RecipientName || '',
          jobTitle: jobTitle || letterJSON?.JobTitle || ''
        },
        [mapping.bodyKey]: letterJSON.Body
      }],
      letterTypes: { 
        coverLetter: mapping.letterTypeKey === 'coverLetter', 
        thankYouLetter: mapping.letterTypeKey === 'thankYouLetter', 
        outreachLetter: mapping.letterTypeKey === 'outreachLetter' 
      },
      templateId: selectedDocmosisTemplate,
      outputFormat: 'pdf'
    };

    const letterResponse = await BulkLetterService.generateBulkLetters(letterRequest);
    if (letterResponse.success && letterResponse.files.length > 0) {
      const file = letterResponse.files[0];
      const binaryString = atob(file.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new Blob([bytes], { type: 'application/pdf' });
    }
    return null;
  };

  if (isLoadingFromDatabase) {
    return (
      <div className="min-h-screen bg-background w-full flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <h2 className="text-xl font-semibold text-foreground">TailorWiz is Loading</h2>
          </div>
          <p className="text-muted-foreground">Retrieving your tailored documents...</p>
        </div>
      </div>
    );
  }

  // Phase 1: Show error if runId fetch failed
  if (runIdError) {
    return (
      <div className="min-h-screen bg-background w-full">
        <PageHeader
          title="Error Loading Run"
          description="Could not load the requested run"
        />
        <div className="container mx-auto py-8">
          <Card>
            <CardContent className="p-8 text-center space-y-6">
              <div className="w-16 h-16 mx-auto bg-destructive/10 rounded-full flex items-center justify-center">
                <ArrowLeft className="w-8 h-8 text-destructive" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-destructive">Failed to Load Run</h2>
                <p className="text-muted-foreground">{runIdError}</p>
              </div>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/resume-tailoring-fixed-duplicate')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Resume Tailoring
                </Button>
                <Button variant="outline" onClick={() => navigate('/admin/pipelines')}>
                  View Run History
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!tailoringResults) {
    const runIdParam = searchParams.get('runId');
    console.log('No tailoringResults found, showing fallback');
    console.log('Available location.state keys:', Object.keys(location.state || {}));
    return (
      <div className="min-h-screen bg-background w-full">
        <PageHeader
          title="No Results"
          description="No tailoring results available to display"
        />
        <div className="container mx-auto py-8 space-y-6">
            
            <Card>
              <CardContent className="p-8 text-center space-y-6">
                <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center">
                  <FileText className="w-8 h-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold">No Results Available</h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    {runIdParam 
                      ? `Run ID "${runIdParam.substring(0, 8)}..." not found or unauthorized.`
                      : 'No tailoring results were provided via navigation. Run tailoring first, or open a specific run from history.'}
                  </p>
                </div>

                <div className="flex gap-4 justify-center flex-wrap">
                  <Button onClick={() => navigate('/resume-tailoring-fixed-duplicate')}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Resume Tailoring
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/pipelines')}>
                    View Run History
                  </Button>

                  <Dialog open={showJsonDialog} onOpenChange={setShowJsonDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <Code className="mr-2 h-4 w-4" />
                        Load JSON
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[80vh]">
                      <DialogHeader>
                        <DialogTitle>Load Resume JSON Data</DialogTitle>
                        <DialogDescription>
                          Paste your resume JSON data below to preview it in the Professional Classic template.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Textarea
                          placeholder="Paste your JSON data here..."
                          value={jsonInput}
                          onChange={(e) => setJsonInput(e.target.value)}
                          className="font-mono text-sm min-h-[400px]"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowJsonDialog(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleLoadJson} disabled={!jsonInput.trim()}>
                            Load Data
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
            
            {jsonResumeData && (
              <div className="space-y-4 bg-gray-100 p-8">
                <div className="flex justify-between items-center max-w-[8.5in] mx-auto">
                  <h3 className="text-lg font-semibold">Resume Template Preview (0.5" margins)</h3>
                  <div className="flex items-center gap-2">
                    <Select value={selectedTemplate} onValueChange={(value: any) => setSelectedTemplate(value)}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Select template" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="template1">Professional Classic</SelectItem>
                        <SelectItem value="template2">Modern Blue</SelectItem>
                        <SelectItem value="template3">Executive Purple</SelectItem>
                        <SelectItem value="template4">Elegant Emerald</SelectItem>
                        <SelectItem value="template5">Minimalist</SelectItem>
                      </SelectContent>
                    </Select>
                    <Badge>Sample Data</Badge>
                  </div>
                </div>
                
                {/* Zoom Controls */}
                <div className="flex items-center justify-center gap-4 max-w-[8.5in] mx-auto">
                  <ZoomOut className="h-4 w-4 text-muted-foreground" />
                  <div className="flex items-center gap-3 flex-1 max-w-xs">
                    <Slider
                      value={[zoomLevel]}
                      onValueChange={(value) => setZoomLevel(value[0])}
                      min={25}
                      max={200}
                      step={5}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium min-w-[3rem] text-right">{zoomLevel}%</span>
                  </div>
                  <ZoomIn className="h-4 w-4 text-muted-foreground" />
                </div>
                
                <div 
                  className="flex justify-center"
                  style={{
                    transform: `scale(${zoomLevel / 100})`,
                    transformOrigin: 'top center',
                    transition: 'transform 0.2s ease-out'
                  }}
                >
                  {selectedTemplate === 'template1' && (
                    <PaginatedTemplate1 
                      data={jsonResumeData} 
                      margins={{ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }}
                    />
                  )}
                  {selectedTemplate === 'template2' && (
                    <PaginatedTemplate2 
                      data={jsonResumeData} 
                      margins={{ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }}
                    />
                  )}
                  {selectedTemplate === 'template3' && (
                    <PaginatedTemplate3 
                      data={jsonResumeData} 
                      margins={{ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }}
                    />
                  )}
                  {selectedTemplate === 'template4' && (
                    <PaginatedTemplate4 
                      data={jsonResumeData} 
                      margins={{ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }}
                    />
                  )}
                  {selectedTemplate === 'template5' && (
                    <PaginatedTemplate5 
                      data={jsonResumeData} 
                      margins={{ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

  return (
    <div className="min-h-screen bg-background w-full">
      <PageHeader 
        title="Tailored Documents" 
        description="Your customized resume and documents are ready"
        backTo="/resume-tailoring-fixed-duplicate"
        variant="blue"
      />

      <div className="flex w-full h-[calc(100vh-73px)]">

        {/* Left Side - Analysis Section */}
        <div className="w-[45%] min-w-0 border-r border-border overflow-y-auto p-6 lg:p-5 md:p-4 space-y-6 lg:space-y-5 md:space-y-4">
          {/* Data Provenance card removed - was used for debugging */}
          
          {/* Timer Display During Template Conversion */}
          {isConvertingToJson && conversionElapsedTime > 0 && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-center gap-3 text-lg font-semibold">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span>Converting to Template: {Math.floor(conversionElapsedTime / 60)}m {conversionElapsedTime % 60}s</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Enhanced Score Analysis Section - Emerald Luxury Design */}
          {/* Show analysis when scores are available, otherwise show a message */}
          {(beforeScore && afterScore) ? (
            <div className="bg-white rounded-3xl p-8 shadow-[0_20px_60px_rgba(0,0,0,0.12)] border-2 border-slate-400/50 ring-1 ring-blue-200/50">
              {/* Panel Header */}
              <div className="flex items-center justify-between gap-3 mb-7">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center text-white shadow-lg shrink-0">
                    <BarChart3 className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Resume Match Analysis
                  </h2>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 shadow-lg shrink-0">
                  <CheckCircle className="w-4 h-4 shrink-0" />
                  Successfully Optimized!
                </div>
              </div>

              {/* Job Info Section */}
              <div className="bg-emerald-50/50 border-2 border-emerald-300/60 rounded-2xl p-5 mb-7">
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col gap-1.5 min-w-0">
                    <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Position</span>
                    <span className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{jobTitle}</span>
                  </div>
                  <div className="flex flex-col gap-1.5 min-w-0">
                    <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Company</span>
                    <span className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{companyName}</span>
                  </div>
                </div>
              </div>

              {/* Stats Grid - Stretched */}
              <div className="grid grid-cols-3 gap-5 mb-7">
                {/* Original Resume Card */}
                <div className="bg-white border-2 border-red-500 rounded-2xl p-6 flex flex-col items-center gap-4 shadow-lg shadow-red-500/15 hover:shadow-red-500/25 hover:-translate-y-1 transition-all relative min-w-0">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white px-4 py-1 rounded-lg text-xs font-extrabold tracking-wider shadow-lg z-10 whitespace-nowrap">
                    BEFORE
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-red-600 mt-2 text-center">Original Resume</h3>
                  <div className="w-32 h-32 relative shrink-0">
                    <div 
                      className="w-full h-full rounded-full"
                      style={{ background: `conic-gradient(#dc2626 ${beforeScore}%, #e5e7eb ${beforeScore}%)` }}
                    />
                    <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center">
                      <span className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{beforeScore}%</span>
                      <span className="text-xs font-semibold text-slate-600">Match Rate</span>
                    </div>
                  </div>
                  <div className="bg-red-100 text-red-600 border-2 border-red-200 px-4 py-2 rounded-xl text-sm font-bold text-center">
                    {beforeScore >= 70 ? "Good Start" : "Needs Improvement"}
                  </div>
                </div>

                {/* Tailored Resume Card */}
                <div className="bg-white border-2 border-emerald-500 rounded-2xl p-6 flex flex-col items-center gap-4 shadow-lg shadow-emerald-500/15 hover:shadow-emerald-500/25 hover:-translate-y-1 transition-all relative min-w-0">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-4 py-1 rounded-lg text-xs font-extrabold tracking-wider shadow-lg z-10 whitespace-nowrap">
                    AFTER
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-emerald-600 mt-2 text-center">Tailored Resume</h3>
                  <div className="w-32 h-32 relative shrink-0">
                    <div 
                      className="w-full h-full rounded-full"
                      style={{ background: `conic-gradient(#059669 ${afterScore}%, #e5e7eb ${afterScore}%)` }}
                    />
                    <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center">
                      <span className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{afterScore}%</span>
                      <span className="text-xs font-semibold text-slate-600">Match Rate</span>
                    </div>
                  </div>
                  <div className="bg-emerald-100 text-emerald-600 border-2 border-emerald-200 px-4 py-2 rounded-xl text-sm font-bold text-center">
                    {afterScore >= 85 ? "Excellent Match!" : "Strong Match!"}
                  </div>
                </div>

                {/* Performance Boost Card */}
                <div className="bg-white border-2 border-purple-500 rounded-2xl p-6 flex flex-col items-center gap-4 shadow-lg shadow-purple-500/15 hover:shadow-purple-500/25 hover:-translate-y-1 transition-all min-w-0">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-purple-600 text-center">Performance Boost</h3>
                  <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl p-5 w-full flex flex-col items-center gap-3 shadow-lg">
                    <span className="text-4xl font-extrabold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>+{improvement}</span>
                    <span className="text-sm font-semibold text-white/90">Points Gained</span>
                    <div className="w-full h-2.5 bg-white/20 rounded-full overflow-hidden">
                      <div className="h-full bg-white rounded-full" style={{ width: `${afterScore}%` }} />
                    </div>
                    <span className="text-xs text-white/90">Progress: {afterScore}%</span>
                  </div>
                </div>
              </div>

              {/* Achievement Banner */}
              <div className="bg-blue-50/50 border-2 border-blue-300/60 rounded-2xl p-6 mb-7">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center text-white shrink-0">
                    <Target className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Outstanding Optimization Achieved!
                  </h3>
                </div>
                <p className="text-slate-500 leading-relaxed mb-4 text-sm">
                  Your resume has been strategically enhanced, increasing your match compatibility from{' '}
                  <span className="font-semibold text-slate-900">{beforeScore}% to {afterScore}%</span> – a remarkable improvement of{' '}
                  <span className="font-semibold text-slate-900">+{improvement} points!</span>{' '}
                  This optimization significantly strengthens your candidacy for the {jobTitle} position at {companyName}.
                </p>
                <div className="flex gap-3 flex-wrap">
                  <div className="bg-green-100 text-green-600 border-2 border-green-200 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 shrink-0" />
                    ATS Optimized
                  </div>
                  <div className="bg-emerald-100 text-emerald-600 border-2 border-emerald-200 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 shrink-0" />
                    Keyword Enhanced
                  </div>
                  <div className="bg-purple-100 text-purple-600 border-2 border-purple-200 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 shrink-0" />
                    Performance Boosted
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <Button 
                  onClick={() => setShowAnalysisModal(true)}
                  className="bg-blue-800 hover:bg-blue-900 text-white font-semibold gap-2 px-5 py-3 rounded-xl shadow-lg shadow-blue-800/25 hover:shadow-blue-800/35 transition-all text-sm h-auto"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  <BarChart3 className="w-4 h-4 shrink-0" />
                  Analysis Report
                </Button>
                
                <Button 
                  onClick={() => navigate('/?view=resume-library', { state: { autoSelectTailored: true } })}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-2 px-5 py-3 rounded-xl shadow-lg shadow-emerald-600/25 hover:shadow-emerald-600/35 transition-all text-sm h-auto"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  <CheckCircle className="w-4 h-4 shrink-0" />
                  Resume Library
                </Button>
              </div>
            </div>
          ) : docmosisGenerationError ? (
            /* Document generation failed: the Resume_JSON that would be
               scored is the SAME one that was too broken to render into an
               actual document. A score computed from it describes nothing
               real, so it must never be shown as if it were. No analysis
               call is made here either — that call costs real API credits
               and would be spent scoring data already known to be invalid. */
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4 text-red-700 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Analysis not available</p>
                    <p className="text-sm mt-1">Document generation failed for this resume, so a match score would be scoring data known to be incomplete. Fix the document first (see the error above), then the analysis can run on the real result.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : analysisInputs && !dbStage4 && !finalDocumentReady ? (
            /* The resume document is not final yet (still generating, or the
               auto Layout QA pass is still re-rendering it). The score is only
               computed from the finished document, so wait here. */
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4 text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <Loader2 className="w-5 h-5 text-slate-500 animate-spin shrink-0" />
                  <div>
                    <p className="font-semibold">Match score runs after the resume document is finished</p>
                    <p className="text-sm mt-1 text-slate-500">The score is measured on the final Word document — the job you selected, your original resume, and the tailored resume exactly as it will download.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : analysisInputs ? (
            /* Claude Direct engine — auto-runs its own richer Tailoring Analysis.
               On a library re-open the saved stage-4 result displays instantly
               (initialData) instead of re-running the analysis. */
            <ClaudeDirectAnalysis
              inputs={analysisInputs}
              job_title={location.state?.jobTitle ?? dbResultsState?.jobTitle ?? null}
              company_name={location.state?.companyName ?? dbResultsState?.companyName ?? null}
              job_url={location.state?.jobUrl ?? dbResultsState?.jobUrl ?? null}
              initialData={dbStage4}
              onData={persistStage4}
            />
          ) : (
            /* No scores available - show informative message */
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4 text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Match Analysis Unavailable</h3>
                    <p className="text-sm text-yellow-600">
                      No match analysis was saved for this tailoring session — older
                      sessions did not capture the analysis inputs. The tailored
                      resume content is still available.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {Array.isArray((location.state as any)?.buildCriteria ?? dbResultsState?.buildCriteria) && ((location.state as any)?.buildCriteria ?? dbResultsState?.buildCriteria).length > 0 && (
            /* Build Criteria — every selection made in the tailoring wizard,
               step by step, including any Advanced Tailoring options. */
            <Card className="mb-6">
              <CardContent className="pt-6">
                <button
                  type="button"
                  onClick={() => setShowBuildCriteria(v => !v)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <h3 className="font-semibold text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <ClipboardList className="w-4 h-4 text-primary" />
                    Build Criteria
                  </h3>
                  <span className="text-sm text-muted-foreground">{showBuildCriteria ? 'Hide' : 'Show'}</span>
                </button>
                {showBuildCriteria && (
                  <div className="mt-4 space-y-4">
                    {(((location.state as any)?.buildCriteria ?? dbResultsState?.buildCriteria) as Array<{ step: string; items: Array<{ label: string; value: string }> }>).map((sec) => (
                      <div key={sec.step}>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{sec.step}</div>
                        <dl className="space-y-1">
                          {sec.items.map((it, i) => (
                            <div key={i} className="flex gap-2 text-sm">
                              <dt className="font-medium shrink-0">{it.label}:</dt>
                              <dd className="text-muted-foreground break-words min-w-0">{it.value}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        </div>

        {/* Right Side - MS Word Document Preview */}
        <div className="w-[55%] min-w-0 overflow-hidden flex flex-col">
          <Card className="flex-1 flex flex-col">
            <CardHeader className="pb-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-blue-600" />
                  Document Preview
                  {isGeneratingDocmosis && (
                    <Badge variant="secondary" className="ml-2 animate-pulse">
                      Generating...
                    </Badge>
                  )}
                  {lettersGenerating && (
                    <Badge className="ml-2 animate-pulse bg-blue-100 text-blue-800 border border-blue-300">
                      ✍️ Writing companion letters…
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {/* Resume Template Selector - Only show when resume tab is active */}
                  {activeDocType === 'resume' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-2 bg-purple-50 border-2 border-purple-500 text-purple-900 hover:bg-purple-100 hover:border-purple-600 font-semibold shadow-sm"
                          disabled={isGeneratingDocmosis}
                        >
                          <CheckCircle className="h-4 w-4 text-purple-600" />
                          <span className="text-xs font-normal text-purple-700">Template:</span>
                          <span className="font-semibold">
                            {isCustomTemplateId(selectedDocmosisTemplate)
                              ? (customTemplates.find(t => t.id === selectedDocmosisTemplate)?.template_name || 'Custom')
                              : docmosisTemplateLabel(selectedDocmosisTemplate)}
                          </span>
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-72 bg-white z-50 max-h-96 overflow-y-auto p-1">
                        <div className="px-3 py-2 mb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b">
                          Choose Resume Template
                        </div>
                        <TemplateFamilyMenu selectedId={selectedDocmosisTemplate} onPick={handleRegenerateWithTemplate} />
                        {[{ ...ORIGINAL_FORMAT_OPTION, subtitle: originalDocxUrl ? ORIGINAL_FORMAT_OPTION.subtitle : 'Upload the Word (.docx) resume whose design you want' } as unknown as typeof DOCMOSIS_TEMPLATE_OPTIONS[number]].map((opt) => {
                          const isSelected = selectedDocmosisTemplate === opt.id;
                          return (
                            <DropdownMenuItem
                              key={opt.id}
                              onClick={() => {
                                if ((opt.id as string) === ORIGINAL_FORMAT_OPTION.id && !originalDocxUrl) { originalUploadRef.current?.click(); return; }
                                handleRegenerateWithTemplate(opt.id);
                              }}
                              className={
                                isSelected
                                  ? 'bg-purple-100 border-l-4 border-purple-600 my-1 rounded-r focus:bg-purple-100'
                                  : 'border-l-4 border-transparent my-1'
                              }
                            >
                              <FileText className={`h-4 w-4 mr-2 ${isSelected ? 'text-purple-700' : 'text-muted-foreground'}`} />
                              <div className="flex flex-col flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium ${isSelected ? 'text-purple-900' : ''}`}>{opt.label}</span>
                                  {isSelected && (
                                    <Badge className="bg-purple-600 hover:bg-purple-600 text-white text-[10px] px-1.5 py-0 h-4 font-bold">
                                      CURRENT
                                    </Badge>
                                  )}
                                </div>
                                <span className={`text-xs ${isSelected ? 'text-purple-700' : 'text-muted-foreground'}`}>{opt.subtitle}</span>
                              </div>
                              {isSelected && (
                                <CheckCircle className="h-4 w-4 ml-2 text-purple-600 flex-shrink-0" />
                              )}
                            </DropdownMenuItem>
                          );
                        })}
                        {/* Wizard / Custom template entries removed from this
                            dropdown per Marcus (Sep 2026) — the curated list
                            above is the full offering. */}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  
                  {/* Session Details Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSessionDetails(true)}
                    className="gap-2"
                  >
                    <Clock className="w-4 h-4" />
                    Session
                  </Button>
                  
                  {/* Download Options Dropdown - Matching WordDocumentViewer */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" className="bg-white border-gray-300 hover:bg-green-50 hover:border-green-400 shadow-sm">
                        <Download className="h-4 w-4 mr-2" />
                        Download Options
                        <ChevronDown className="h-3 w-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64 bg-white z-50">
                      {/* Download This Document */}
                      <DropdownMenuItem className="font-medium text-sm text-muted-foreground px-2 py-1" disabled>
                        Download This Document
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => generatedWordDocs[activeDocType] && handleDownloadGeneratedDoc(activeDocType)}
                        disabled={!generatedWordDocs[activeDocType]}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        As Word Document
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleExportPDF} disabled={isExportingPDF}>
                        <FileType className="h-4 w-4 mr-2" />
                        As PDF Document
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={async () => {
                          if (generatedWordDocs[activeDocType]) {
                            handleDownloadGeneratedDoc(activeDocType);
                            await handleExportPDF();
                          }
                        }}
                        disabled={!generatedWordDocs[activeDocType] || isExportingPDF}
                      >
                        <FileDown className="h-4 w-4 mr-2" />
                        As Both (Word + PDF)
                      </DropdownMenuItem>
                      
                      {/* Download All Documents - Only show if multiple docs */}
                      {Object.keys(generatedWordDocs).length > 1 && (
                        <>
                          <div className="border-t my-1"></div>
                          <DropdownMenuItem className="font-medium text-sm text-muted-foreground px-2 py-1" disabled>
                            Download All Documents
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              Object.keys(generatedWordDocs).forEach((key, index) => {
                                setTimeout(() => {
                                  handleDownloadGeneratedDoc(key as keyof typeof generatedWordDocs);
                                }, index * 500);
                              });
                            }}
                          >
                            <Package className="h-4 w-4 mr-2" />
                            All as Word (Individual)
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={async () => {
                              const docTypes = Object.keys(generatedWordDocs);
                              for (let i = 0; i < docTypes.length; i++) {
                                setActiveDocType(docTypes[i] as keyof typeof generatedWordDocs);
                                await new Promise(resolve => setTimeout(resolve, 300));
                                await handleExportPDF();
                              }
                            }}
                          >
                            <Package className="h-4 w-4 mr-2" />
                            All as PDF (Individual)
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={async () => {
                              const docTypes = Object.keys(generatedWordDocs);
                              // Download all Word docs first
                              docTypes.forEach((key, index) => {
                                setTimeout(() => {
                                  handleDownloadGeneratedDoc(key as keyof typeof generatedWordDocs);
                                }, index * 500);
                              });
                              // Then download all PDFs
                              for (let i = 0; i < docTypes.length; i++) {
                                setActiveDocType(docTypes[i] as keyof typeof generatedWordDocs);
                                await new Promise(resolve => setTimeout(resolve, 500));
                                await handleExportPDF();
                              }
                            }}
                          >
                            <Package className="h-4 w-4 mr-2" />
                            All as Both (Individual)
                          </DropdownMenuItem>
                          <div className="border-t my-1"></div>
                          <DropdownMenuItem 
                            onClick={async () => {
                              // Download all as Word ZIP package
                              const JSZip = (await import('jszip')).default;
                              const zip = new JSZip();
                              const folderName = `${personName || 'Documents'} - ${jobTitle || 'Documents'}`;
                              const wordFolder = zip.folder(folderName);
                              
                              for (const [key, docData] of Object.entries(generatedWordDocs)) {
                                if (docData?.publicUrl) {
                                  try {
                                    const response = await fetch(docData.publicUrl);
                                    if (response.ok) {
                                      const blob = await response.blob();
                                      const cleanFilename = generateCleanFilename(key, 'docx');
                                      wordFolder?.file(cleanFilename, blob);
                                    }
                                  } catch (error) {
                                    console.error(`Failed to add ${key} to ZIP:`, error);
                                  }
                                }
                              }
                              
                              const zipBlob = await zip.generateAsync({ type: 'blob' });
                              const zipUrl = URL.createObjectURL(zipBlob);
                              const link = document.createElement('a');
                              link.href = zipUrl;
                              link.download = `${folderName}.zip`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(zipUrl);
                              toast({ title: "ZIP Downloaded", description: "Word documents ZIP package downloaded" });
                            }}
                          >
                            <Package className="h-4 w-4 mr-2" />
                            ZIP Package (Word)
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={async () => {
                              // Download all as PDF ZIP package via Docmosis native rendering
                              const JSZip = (await import('jszip')).default;
                              const zip = new JSZip();
                              const folderName = `${personName || 'Documents'} - ${jobTitle || 'Documents'} PDFs`;
                              const pdfFolder = zip.folder(folderName);
                              
                              toast({ title: "Generating PDFs", description: "Please wait while PDFs are generated via Docmosis..." });
                              
                              for (const key of Object.keys(generatedWordDocs)) {
                                try {
                                  const prevActiveDocType = activeDocType;
                                  setActiveDocType(key as keyof typeof generatedWordDocs);
                                  // Generate PDF via the same handleExportPDF logic but inline
                                  const pdfBlob = await generateDocmosisPdfBlob(key);
                                  if (pdfBlob) {
                                    const cleanFilename = generateCleanFilename(key, 'pdf');
                                    pdfFolder?.file(cleanFilename, pdfBlob);
                                  }
                                  setActiveDocType(prevActiveDocType);
                                } catch (error) {
                                  console.error(`Failed to generate PDF for ${key}:`, error);
                                }
                              }
                              
                              const zipBlob = await zip.generateAsync({ type: 'blob' });
                              const zipUrl = URL.createObjectURL(zipBlob);
                              const link = document.createElement('a');
                              link.href = zipUrl;
                              link.download = `${folderName}.zip`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(zipUrl);
                              toast({ title: "ZIP Downloaded", description: "PDF documents ZIP package downloaded" });
                            }}
                          >
                            <Package className="h-4 w-4 mr-2" />
                            ZIP Package (PDF)
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={async () => {
                              // Download all as Both (Word + PDF) ZIP package
                              const JSZip = (await import('jszip')).default;
                              const zip = new JSZip();
                              const folderName = `${personName || 'Documents'} - ${jobTitle || 'Documents'}`;
                              const wordFolder = zip.folder(`${folderName}/Word`);
                              const pdfFolder = zip.folder(`${folderName}/PDF`);
                              
                              toast({ title: "Preparing Package", description: "Please wait while documents are prepared..." });
                              
                              for (const [key, docData] of Object.entries(generatedWordDocs)) {
                                if (docData?.publicUrl) {
                                  try {
                                    // Add Word doc
                                    const wordResponse = await fetch(docData.publicUrl);
                                    if (wordResponse.ok) {
                                      const wordBlob = await wordResponse.blob();
                                      const wordFilename = generateCleanFilename(key, 'docx');
                                      wordFolder?.file(wordFilename, wordBlob);
                                    }
                                    
                                    // Generate PDF via Docmosis native rendering
                                    const pdfBlob = await generateDocmosisPdfBlob(key);
                                    if (pdfBlob) {
                                      const pdfFilename = generateCleanFilename(key, 'pdf');
                                      pdfFolder?.file(pdfFilename, pdfBlob);
                                    }
                                  } catch (error) {
                                    console.error(`Failed to process ${key}:`, error);
                                  }
                                }
                              }
                              
                              const zipBlob = await zip.generateAsync({ type: 'blob' });
                              const zipUrl = URL.createObjectURL(zipBlob);
                              const link = document.createElement('a');
                              link.href = zipUrl;
                              link.download = `${folderName} - Complete.zip`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(zipUrl);
                              toast({ title: "ZIP Downloaded", description: "Complete document package (Word + PDF) downloaded" });
                            }}
                          >
                            <Package className="h-4 w-4 mr-2" />
                            ZIP Package (Both)
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>

                        <input
                          ref={originalUploadRef}
                          type="file"
                          accept=".docx"
                          className="hidden"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            if (!f) return;
                            if (!/\.docx$/i.test(f.name)) {
                              toast({ title: 'Please choose a Word (.docx) file', description: 'Your design is copied from the Word file. A PDF cannot be copied.', variant: 'destructive' });
                              return;
                            }
                            try {
                              const { ResumeLibraryService } = await import('@/services/resumeLibraryService');
                              const savedR: any = await ResumeLibraryService.saveResumeWithFile(f.name.replace(/\.[^/.]+$/, ''), f);
                              const url = savedR?.originalFileUrl;
                              if (!url) throw new Error('upload failed');
                              setUploadedOriginalUrl(url);
                              BulkResumeService.originalDocxUrl = url;
                              await handleRegenerateWithTemplate(ORIGINAL_FORMAT_OPTION.id);
                            } catch (err) {
                              console.error('Original design upload failed:', err);
                              toast({ title: "Sorry, we couldn't use that file right now", description: 'Please try again in a few minutes.', variant: 'destructive' });
                            }
                          }}
                        />
                  {/* View/Edit in Full Word Viewer */}
                  <Button
                    onClick={handleOpenInWordViewer}
                    size="sm"
                    className="bg-blue-900 hover:bg-blue-800 text-white gap-2"
                  >
                    <Monitor className="w-4 h-4" />
                    Full Editor
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="flex-1 flex flex-col p-4 pt-0 relative">
              {/* NOT FINISHED notice (Marcus, Oct 25 2026): while the resume is
                  still being fitted, formatted or re-rendered, say so clearly
                  over the preview so nobody downloads or judges a draft. */}
              {activeDocType === 'resume' && (isFitting || isGeneratingDocmosis || namedGroupsBusy || (autoFixFormatting && docmosisGenerationAttempted && !autoFitFinished && !!generatedWordDocs.resume)) && (
                <div className="absolute inset-0 z-20 flex items-start justify-center pt-24 bg-white/60 backdrop-blur-[1px]">
                  <div className="flex items-center gap-3 rounded-xl border-2 border-amber-400 bg-amber-50 px-6 py-4 shadow-xl">
                    <span className="h-5 w-5 rounded-full border-2 border-amber-300 border-t-amber-600 animate-spin" />
                    <div>
                      <div className="text-base font-bold text-amber-900">Still formatting your resume</div>
                      <div className="text-sm text-amber-800">Fitting every line to the page. Please wait before downloading.</div>
                    </div>
                  </div>
                </div>
              )}
              {/* Document Type Tabs */}
              {(Object.keys(generatedWordDocs).length > 1 || structuredBio) && (
                <div className="flex gap-2 mb-4 pb-3 border-b flex-wrap">
                  {generatedWordDocs.resume && (
                    <Button
                      size="sm"
                      variant={activeDocType === 'resume' ? 'default' : 'outline'}
                      onClick={() => setActiveDocType('resume')}
                      className={activeDocType === 'resume' ? 'bg-blue-900' : ''}
                    >
                      Resume
                    </Button>
                  )}
                  {generatedWordDocs.coverLetter && (
                    <Button
                      size="sm"
                      variant={activeDocType === 'coverLetter' ? 'default' : 'outline'}
                      onClick={() => setActiveDocType('coverLetter')}
                      className={activeDocType === 'coverLetter' ? 'bg-emerald-700' : ''}
                    >
                      Cover Letter
                    </Button>
                  )}
                  {generatedWordDocs.thankYouLetter && (
                    <Button
                      size="sm"
                      variant={activeDocType === 'thankYouLetter' ? 'default' : 'outline'}
                      onClick={() => setActiveDocType('thankYouLetter')}
                      className={activeDocType === 'thankYouLetter' ? 'bg-purple-700' : ''}
                    >
                      Thank You
                    </Button>
                  )}
                  {generatedWordDocs.outreachLetter && (
                    <Button
                      size="sm"
                      variant={activeDocType === 'outreachLetter' ? 'default' : 'outline'}
                      onClick={() => setActiveDocType('outreachLetter')}
                      className={activeDocType === 'outreachLetter' ? 'bg-orange-700' : ''}
                    >
                      Outreach
                    </Button>
                  )}
                  {generatedWordDocs.linkedin && (
                    <Button
                      size="sm"
                      variant={activeDocType === 'linkedin' ? 'default' : 'outline'}
                      onClick={() => setActiveDocType('linkedin')}
                      className={activeDocType === 'linkedin' ? 'bg-sky-700' : ''}
                    >
                      LinkedIn
                    </Button>
                  )}
                  {structuredBio && (
                    <Button
                      size="sm"
                      variant={activeDocType === 'executiveBio' ? 'default' : 'outline'}
                      onClick={() => setActiveDocType('executiveBio')}
                      className={activeDocType === 'executiveBio' ? 'bg-slate-800' : ''}
                    >
                      Executive Bio
                    </Button>
                  )}
                  {generatedWordDocs.solutionAsset && (
                    <Button
                      size="sm"
                      variant={activeDocType === 'solutionAsset' ? 'default' : 'outline'}
                      onClick={() => setActiveDocType('solutionAsset' as any)}
                      className={activeDocType === ('solutionAsset' as any) ? 'bg-amber-700' : ''}
                    >
                      Solution Asset
                    </Button>
                  )}
                  {generatedWordDocs.hiringManagerReport && (
                    <Button
                      size="sm"
                      variant={activeDocType === ('hiringManagerReport' as any) ? 'default' : 'outline'}
                      onClick={() => setActiveDocType('hiringManagerReport' as any)}
                      className={activeDocType === ('hiringManagerReport' as any) ? 'bg-rose-700' : ''}
                    >
                      Hiring Manager Report
                    </Button>
                  )}
                </div>
              )}

              {/* Word Online Viewer - All viewers rendered but only active one visible to prevent reload */}
              <div className="flex-1 min-h-[600px] rounded-lg overflow-hidden border bg-white relative">
                {isGeneratingDocmosis ? (
                  <div className="h-full flex flex-col items-center justify-center gap-4">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    <div className="text-center">
                      <p className="font-medium text-gray-900">Generating Word Documents...</p>
                      <p className="text-sm text-muted-foreground">This may take 10-30 seconds</p>
                    </div>
                  </div>
                ) : docmosisGenerationError ? (
                  <div className="h-full flex flex-col items-center justify-center gap-4 p-8">
                    <div className="text-center text-red-600">
                      <p className="font-semibold">Document Generation Failed</p>
                      <p className="text-sm mt-2">{docmosisGenerationError}</p>
                    </div>
                    <Button 
                      onClick={() => {
                        setDocmosisGenerationAttempted(false);
                        setDocmosisGenerationError(null);
                      }}
                      variant="outline"
                    >
                      Retry Generation
                    </Button>
                  </div>
                ) : Object.keys(generatedWordDocs).length > 0 ? (
                  <>
                    {/* Navy Executive Bio — rendered in-app (HTML page), exported as image PDF */}
                    {structuredBio && (
                      <div className={`absolute inset-0 overflow-auto bg-gray-200 ${activeDocType === 'executiveBio' ? 'z-10 visible' : 'z-0 invisible'}`}>
                        <div className="flex items-center gap-3 p-3 bg-white border-b sticky top-0 z-20">
                          <label className="text-xs font-medium cursor-pointer text-blue-700 hover:underline">
                            {bioPhoto || structuredBio.photoUrl ? 'Change headshot' : 'Add headshot'}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                const r = new FileReader();
                                r.onload = () => setBioPhoto(String(r.result));
                                r.readAsDataURL(f);
                              }}
                            />
                          </label>
                          <Button
                            size="sm"
                            disabled={bioExporting || bioFitOk === false}
                            onClick={async () => {
                              if (!bioPageRef.current) return;
                              setBioExporting(true);
                              try {
                                await exportExecutiveBioPdf(bioPageRef.current, structuredBio.name);
                              } catch (err: any) {
                                toast({ title: 'Bio PDF export failed', description: err?.message ?? String(err), variant: 'destructive' });
                              } finally {
                                setBioExporting(false);
                              }
                            }}
                          >
                            {bioExporting ? 'Exporting…' : 'Download PDF'}
                          </Button>
                          {bioFitOk === false && (
                            <span className="text-xs text-red-600">
                              Content does not fit on one page even at minimum size. The bio copy needs shortening before export.
                            </span>
                          )}
                        </div>
                        <div className="flex justify-center py-4">
                          <div style={{ width: 1275 * 0.62, height: 1650 * 0.62, overflow: 'hidden' }}>
                            <div style={{ transform: 'scale(0.62)', transformOrigin: 'top left' }}>
                              <div ref={bioPageRef} style={{ width: 1275 }}>
                                <ExecutiveBioNavy
                                  data={structuredBio}
                                  photoUrl={bioPhoto}
                                  onFitResult={(r) => setBioFitOk(r.ok)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Render all viewers but show/hide based on activeDocType to prevent iframe reload */}
                    {generatedWordDocs.resume?.publicUrl && (
                      <div className={`absolute inset-0 ${activeDocType === 'resume' ? 'z-10 visible' : 'z-0 invisible'}`}>
                        <WordOnlineViewer
                          documentUrl={generatedWordDocs.resume.publicUrl}
                          fileName={generatedWordDocs.resume.filename}
                          className="w-full h-full"
                          height="100%"
                        />
                      </div>
                    )}
                    {generatedWordDocs.coverLetter?.publicUrl && (
                      <div className={`absolute inset-0 ${activeDocType === 'coverLetter' ? 'z-10 visible' : 'z-0 invisible'}`}>
                        <WordOnlineViewer
                          documentUrl={generatedWordDocs.coverLetter.publicUrl}
                          fileName={generatedWordDocs.coverLetter.filename}
                          className="w-full h-full"
                          height="100%"
                        />
                      </div>
                    )}
                    {generatedWordDocs.thankYouLetter?.publicUrl && (
                      <div className={`absolute inset-0 ${activeDocType === 'thankYouLetter' ? 'z-10 visible' : 'z-0 invisible'}`}>
                        <WordOnlineViewer
                          documentUrl={generatedWordDocs.thankYouLetter.publicUrl}
                          fileName={generatedWordDocs.thankYouLetter.filename}
                          className="w-full h-full"
                          height="100%"
                        />
                      </div>
                    )}
                    {generatedWordDocs.outreachLetter?.publicUrl && (
                      <div className={`absolute inset-0 ${activeDocType === 'outreachLetter' ? 'z-10 visible' : 'z-0 invisible'}`}>
                        <WordOnlineViewer
                          documentUrl={generatedWordDocs.outreachLetter.publicUrl}
                          fileName={generatedWordDocs.outreachLetter.filename}
                          className="w-full h-full"
                          height="100%"
                        />
                      </div>
                    )}
                    {generatedWordDocs.linkedin?.publicUrl && (
                      <div className={`absolute inset-0 ${activeDocType === 'linkedin' ? 'z-10 visible' : 'z-0 invisible'}`}>
                        <WordOnlineViewer
                          documentUrl={generatedWordDocs.linkedin.publicUrl}
                          fileName={generatedWordDocs.linkedin.filename}
                          className="w-full h-full"
                          height="100%"
                        />
                      </div>
                    )}
                    {generatedWordDocs.solutionAsset?.publicUrl && (
                      <div className={`absolute inset-0 ${activeDocType === 'solutionAsset' ? 'z-10 visible' : 'z-0 invisible'}`}>
                        <WordOnlineViewer
                          documentUrl={generatedWordDocs.solutionAsset.publicUrl}
                          fileName={generatedWordDocs.solutionAsset.filename}
                          className="w-full h-full"
                          height="100%"
                        />
                      </div>
                    )}
                    {generatedWordDocs.hiringManagerReport?.publicUrl && (
                      <div className={`absolute inset-0 ${activeDocType === 'hiringManagerReport' ? 'z-10 visible' : 'z-0 invisible'}`}>
                        <WordOnlineViewer
                          documentUrl={generatedWordDocs.hiringManagerReport.publicUrl}
                          fileName={generatedWordDocs.hiringManagerReport.filename}
                          className="w-full h-full"
                          height="100%"
                        />
                      </div>
                    )}
                    {/* Fallback if active type has no URL yet */}
                    {!generatedWordDocs[activeDocType]?.publicUrl && (
                      <div className="h-full flex flex-col items-center justify-center gap-4 relative z-20">
                        <FileText className="w-12 h-12 text-gray-400" />
                        <div className="text-center">
                          <p className="font-medium text-gray-600">No Document Available</p>
                          <p className="text-sm text-muted-foreground">Select a document type above to preview</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center gap-4">
                    <FileText className="w-12 h-12 text-gray-400" />
                    <div className="text-center">
                      <p className="font-medium text-gray-600">No Document Available</p>
                      <p className="text-sm text-muted-foreground">No renderable resume data for this session — older sessions saved the document but not the resume JSON needed to re-render it.</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Fit Edits Modal — before/after of every line the last fit run adjusted */}
      <Dialog open={showFitChanges} onOpenChange={setShowFitChanges}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-emerald-600" />
              {fitEngine} — {fitChanges.length} line{fitChanges.length === 1 ? '' : 's'} edited
            </DialogTitle>
            <DialogDescription>
              Exactly what was adjusted to make the resume fit. Character counts include spaces.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {fitChanges.map((c, i) => (
              <div key={i} className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-sm font-bold text-slate-800">{c.label}</div>
                <div className="px-4 py-3 space-y-2">
                  <div className="rounded-lg bg-rose-50 border border-rose-200 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-rose-600">Before</span>
                      <span className="text-[10px] font-semibold text-rose-500">{c.before.length} chars</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{c.before}</p>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">After</span>
                      <span className="text-[10px] font-semibold text-emerald-600">{c.after.length} chars ({c.after.length - c.before.length >= 0 ? '+' : ''}{c.after.length - c.before.length})</span>
                    </div>
                    <p className="text-sm text-slate-800 leading-relaxed">{c.after}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Session Details Modal */}
        <Dialog open={showSessionDetails} onOpenChange={setShowSessionDetails}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Session Details
              </DialogTitle>
              <DialogDescription>
                Information about this tailoring session
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Created Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Created Date</label>
                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {sessionTimestamp 
                        ? sessionTimestamp.toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : new Date().toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Data Source Indicator - Shows where the data came from */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Data Source</label>
                <div className={`p-3 rounded-lg ${dataSource === 'vectorshift_tailoring' ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant={dataSource === 'vectorshift_tailoring' ? 'default' : 'secondary'} className="text-xs">
                      {dataSource === 'vectorshift_tailoring' ? '✓ VectorShift (Fresh)' : 
                       dataSource === 'database' ? '📁 Database' :
                       dataSource === 'navigation_state' ? '🔄 Navigation State' : dataSource}
                    </Badge>
                    {runId && <span className="text-xs text-muted-foreground font-mono">ID: {runId.slice(0, 8)}...</span>}
                  </div>
                </div>
              </div>

              {/* Tailoring Time - The actual VectorShift processing time (ALWAYS SHOW if available) */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Tailoring Time</label>
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-emerald-600" />
                    <span className="font-mono font-semibold text-emerald-700">
                      {resolvedTailoringDuration || 'Not tracked'}
                    </span>
                    <span className="text-xs text-emerald-600">(AI tailoring)</span>
                  </div>
                </div>
              </div>

              {/* Template Conversion Time - Gemini JSON parsing (secondary, muted) */}
              {conversionElapsedTime > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Template Conversion</label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span className="font-mono">
                        {formatDuration(conversionElapsedTime)}
                      </span>
                      <span>(template formatting)</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Formatting tools (moved out of the toolbar, Marcus Oct 25 2026).
                  Layout fitting already runs automatically in every tailoring run;
                  these are for re-checking after manual edits or older resumes. */}
              {activeDocType === 'resume' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Formatting</label>
                  <div className="p-3 bg-muted/30 rounded-lg flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setShowSessionDetails(false); handleLayoutQa(); }}
                      disabled={isFitting || isGeneratingDocmosis}
                      className="gap-2"
                      title="Measure every line again and fix any that break the rules. Only needed after manual edits or on older resumes."
                    >
                      <Target className="h-4 w-4 text-sky-600" />
                      {isFitting ? 'Fitting…' : 'Re-check line fit'}
                    </Button>
                    {(generatedWordDocs.resume as any)?.polish && (
                      <PolishBadge report={(generatedWordDocs.resume as any).polish} />
                    )}
                    {fitChanges.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setShowSessionDetails(false); setShowFitChanges(true); }}
                        className="gap-2"
                        title="Before/after view of every line the fitting rewrote."
                      >
                        <FileText className="h-4 w-4 text-amber-600" />
                        View edits ({fitChanges.length})
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Document Filename */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Document Filename</label>
                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="font-mono break-all">{generatedFilename}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedFilename);
                        toast({ title: "Filename copied to clipboard" });
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Status Badge */}
              {isFromDatabase && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Session Status</label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <Badge variant="outline" className="text-xs">
                      Loaded from Library
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Detailed Analysis Modal - Using new comprehensive component */}
        <ClaudeMatchAnalysisModal
          open={showAnalysisModal}
          onOpenChange={setShowAnalysisModal}
          analysisResults={claudeMatch.analysisResults || analysisResults}
          changesAnalysis={claudeMatch.analysisResults ? undefined : changesAnalysis}
          beforeScore={beforeScore}
          afterScore={afterScore}
          jobTitle={jobTitle}
          companyName={companyName}
        />
    </div>
  );
};

/** Display-only badge for the docx-polish report that rides on each render.
 *  Green: clean. Amber: fixes were applied (click to see them). Red: blockers
 *  need a rewrite (click to see the instructions). Slate: polish unavailable. */
function PolishBadge({ report }: { report: any }) {
  const [open, setOpen] = useState(false);
  const fixes: any[] = Array.isArray(report?.fixes) ? report.fixes : [];
  const blockers: any[] = Array.isArray(report?.blockers) ? report.blockers : [];
  const unavailable = !!report?.unavailable;
  const tone = unavailable
    ? 'bg-slate-50 border-slate-300 text-slate-600'
    : blockers.length > 0
      ? 'bg-rose-50 border-rose-500 text-rose-900'
      : fixes.length > 0
        ? 'bg-amber-50 border-amber-500 text-amber-900'
        : 'bg-emerald-50 border-emerald-500 text-emerald-900';
  const label = unavailable
    ? 'Polish: n/a'
    : blockers.length > 0
      ? `Polish: ${blockers.length} blocker${blockers.length === 1 ? '' : 's'}`
      : fixes.length > 0
        ? `Polish: ${fixes.length} fix${fixes.length === 1 ? '' : 'es'}`
        : 'Polish: clean';
  return (
    <div className="relative">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        className={`gap-2 border-2 font-semibold shadow-sm ${tone}`}
        title={unavailable ? String(report?.reason ?? 'Polish did not run for this render.') : 'What the document polish pass found on this exact render. Display-only.'}
      >
        {label}
      </Button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-96 max-h-80 overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-xl text-left">
          {unavailable ? (
            <p className="text-sm text-slate-600">{String(report?.reason ?? 'Polish did not run for this render.')}</p>
          ) : (
            <>
              <div className="text-xs text-slate-400 mb-2">
                {report?.template ?? 'Template'} · {report?.modified ? 'document was modified' : 'no changes needed'}
              </div>
              {blockers.length > 0 && (
                <div className="mb-3">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-rose-700 mb-1">Needs a rewrite (too wide to fix in XML)</div>
                  <ul className="space-y-1.5">
                    {blockers.map((b: any, i: number) => (
                      <li key={i} className="text-[13px] text-slate-700 border-l-2 border-rose-300 pl-2">
                        {b.rewriteInstruction ?? b.detail ?? JSON.stringify(b)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {fixes.length > 0 ? (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-amber-700 mb-1">Fixed automatically</div>
                  <ul className="space-y-1">
                    {fixes.map((f: any, i: number) => (
                      <li key={i} className="text-[13px] text-slate-700">• {f.detail ?? f.kind}{typeof f.paragraph === 'number' ? ` (¶${f.paragraph})` : ''}</li>
                    ))}
                  </ul>
                </div>
              ) : blockers.length === 0 ? (
                <p className="text-sm text-emerald-700">Every check passed — nothing needed fixing.</p>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ResumeResultsTemplated;